import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { Table, Tag, Spin, Select, DatePicker, Button, Tooltip } from 'antd'
import {
  PieChart, Pie, Cell, BarChart, Bar,
  LineChart, Line, ScatterChart, Scatter, ZAxis,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import {
  CheckCircleOutlined, ThunderboltOutlined, TeamOutlined,
  BarChartOutlined, AlertOutlined, PieChartOutlined,
  BankOutlined, PlayCircleOutlined, DownloadOutlined, PrinterOutlined,
  TableOutlined, ApartmentOutlined, RiseOutlined, LineChartOutlined,
  DotChartOutlined, CalendarOutlined, TrophyOutlined,
} from '@ant-design/icons'
import adminRequest from 'utils/adminRequest'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

const { RangePicker } = DatePicker
const { Option } = Select

// ── Export helper ───────────────────────────────────────────────
// cols: [{ h: 'Header Label', k: 'dataKey' }, ...]
function exportXlsx(rows, cols, filename) {
  const data = [
    cols.map(c => c.h),
    ...rows.map(r => cols.map(c => r[c.k] ?? '')),
  ]
  const ws = XLSX.utils.aoa_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Data')
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename)
}

// ── Type config ────────────────────────────────────────────────
// Assessment dashboard surfaces 5 types: Communication, Aptitude,
// Role_Based (rendered as "Psychometric"), AI_Interview, Custom_Assessment.
// Tech_MCQ / Tech_Coding were removed from cards, charts, tables, exports,
// and filter dropdowns everywhere — they remain as raw section flavours
// inside role_based_scores but are no longer first-class types here.
const TYPE_CFG = {
  Communication:     { color: '#1e40af', bg: '#dbeafe', chart: '#1e3a8a', tag: 'blue'    },
  Aptitude:          { color: '#166534', bg: '#dcfce7', chart: '#15803d', tag: 'green'   },
  Role_Based:        { color: '#6d28d9', bg: '#ede9fe', chart: '#5b21b6', tag: 'purple'  },
  Psychometric:      { color: '#6d28d9', bg: '#ede9fe', chart: '#5b21b6', tag: 'purple'  },
  AI_Interview:      { color: '#92400e', bg: '#fef3c7', chart: '#78350f', tag: 'orange'  },
  Custom_Assessment: { color: '#0e7490', bg: '#cffafe', chart: '#0891b2', tag: 'cyan'    },
}
const TYPE_KEYS = ['Communication', 'Aptitude', 'Role_Based', 'AI_Interview', 'Custom_Assessment']
const typeCfg = name => TYPE_CFG[name] || { color: '#64748b', bg: '#f1f5f9', chart: '#475569', tag: 'default' }

// ── Sub-tabs ───────────────────────────────────────────────────
// Practice vs Actual is institute-only (corporate flow doesn't have a
// practice mode), so the corporate view drops it from the bar.
// The Entities sub-tab was removed per product — the per-entity drill
// already lives in the new Corporate / Institute dropdown on the
// filter bar, so a separate sub-tab listing entities is redundant.
const ALL_SUB_TABS = [
  'Overview', 'Scores & Rankings', 'Top Candidates', 'Practice vs Actual',
  'Student Growth', 'Performance', 'Analytics', 'Predictive',
  'Cross-Institute', 'Schedule',
]
const subTabsFor = (entity) =>
  entity === 'corporate'
    ? ALL_SUB_TABS.filter(t => t !== 'Practice vs Actual')
    : ALL_SUB_TABS

// ── Helpers ────────────────────────────────────────────────────
const fmt        = n => n == null ? '—' : Number(n).toLocaleString()
const scoreColor    = v => v >= 80 ? '#22c55e' : v >= 70 ? '#84cc16' : v >= 60 ? '#f97316' : '#ef4444'
const utilColor     = v => v >= 80 ? '#22c55e' : v >= 60 ? '#f59e0b' : '#ef4444'
const gradeTagColor = g => ({ 'A+': 'green', A: 'lime', 'B+': 'orange', B: 'gold' }[g] || 'red')

// Short label for X-axis ticks on Top-Candidates score breakdown charts.
// When the row has a real full name we keep the first two words ("Ravi K.");
// when only an email is present we collapse it to the local-part before any
// '+' suffix and cap length so 50-char dev emails don't take over the axis.
const shortCandidateLabel = (fullName, email) => {
  const name = (fullName || '').trim()
  if (name && !name.includes('@')) {
    const parts = name.split(/\s+/)
    return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0]
  }
  const e = (email || name || '').trim()
  if (!e) return '—'
  const local = e.split('@')[0].split('+')[0]
  return local.length > 14 ? `${local.slice(0, 13)}…` : local
}

// ── Styled components ──────────────────────────────────────────

const InnerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`
const InnerTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 10px;
`
const AlertBadge = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 20px;
  padding: 2px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: default;
`
const ActionBtns = styled.div`
  display: flex;
  gap: 8px;
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`
const FilterLabel = styled.span`
  font-size: 12px;
  color: #7b8499;
  font-weight: 500;
  white-space: nowrap;
`
const FilterDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e8ecf4;
  flex-shrink: 0;
`
const StatusToggleGroup = styled.div`
  display: flex;
  gap: 4px;
`
const StatusToggle = styled.button`
  padding: 3px 13px;
  border-radius: 20px;
  border: 1px solid ${p => p.$active ? '#1a1a2e' : '#e8ecf4'};
  background: ${p => p.$active ? '#1a1a2e' : 'transparent'};
  color: ${p => p.$active ? '#ffffff' : '#7b8499'};
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #1a1a2e; color: ${p => p.$active ? '#fff' : '#1a1a2e'}; }
`

const SubTabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e8ecf4;
  margin-bottom: 20px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`
const SubTab = styled.button`
  padding: 9px 16px;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? '#3249D7' : 'transparent'};
  margin-bottom: -2px;
  background: transparent;
  color: ${p => p.$active ? '#3249D7' : '#7b8499'};
  font-size: 13px;
  font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { color: #3249D7; }
`

const KPIRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  margin-bottom: 20px;
`
const KPICard = styled.div`
  flex: 1 1 0;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 14px 16px 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  position: relative;
`
const KPIHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`
const KPILabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #7b8499;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.3;
  padding-right: 4px;
`
const KPIIconWrap = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${p => p.$color || '#3249D7'}18;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: ${p => p.$color || '#3249D7'};
  flex-shrink: 0;
`
const KPIValue = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const KPITrend = styled.div`
  font-size: 11px;
  color: ${p => p.$color || '#7b8499'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 55fr 45fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`
const ChartCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`
const ChartCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`
const ChartCardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 6px;
`
const DonutLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 6px;
`
const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #444;
  font-weight: 500;
`
const LegendSquare = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${p => p.$color};
  flex-shrink: 0;
`

const TableSection = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  margin-bottom: 8px;
`
const TableSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`
const TableSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 6px;
`

const SpinWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
`
const ComingSoon = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #bbb;
`

// ── Inline bar ─────────────────────────────────────────────────
const InlineBar = ({ numVal, displayVal, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 56, height: 5, background: '#f0f2f5', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ width: `${Math.min(numVal, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{displayVal}</span>
  </div>
)

const ScoreTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
`
const ScoreTypeCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-top: 3px solid ${p => p.$color || '#3249D7'};
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`
const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`
const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 480px;
  overflow-y: auto;
  padding-right: 2px;
`
const CandidateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  background: #f8f9fc;
  border-radius: 8px;
`
const RankBadge = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${p => p.$rank === 0 ? '#f59e0b' : p.$rank === 1 ? '#94a3b8' : p.$rank === 2 ? '#c97c2a' : '#e8ecf4'};
  color: ${p => p.$rank < 3 ? '#fff' : '#1a1a2e'};
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ThreeColGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`
const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #f0f2f5;
  &:last-child { border-bottom: none; }
`
const ProgressLabel = styled.span`
  width: 72px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  flex-shrink: 0;
`
const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: #f0f2f5;
  border-radius: 4px;
  overflow: hidden;
`
const ProgressFill = styled.div`
  height: 100%;
  background: #15803d;
  border-radius: 4px;
  width: ${p => p.$pct}%;
  transition: width 0.4s ease;
`
const ProgressCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #7b8499;
  min-width: 72px;
  text-align: right;
`
const TypeFilterGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`
const TypeFilterBtn = styled.button`
  padding: 4px 14px;
  border-radius: 20px;
  border: 1px solid ${p => p.$active ? p.$color || '#1a1a2e' : '#e8ecf4'};
  background: ${p => p.$active ? p.$color || '#1a1a2e' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#7b8499'};
  font-size: 12px;
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  transition: all 0.15s;
`

// ── Component ───────────────────────────────────────────────────
const AssessmentTab = () => {
  // Assessment dashboard is entity-scoped: every endpoint runs against a
  // single entity (corporate OR institute). Default to corporate on mount;
  // user can flip to institute via the page-level dropdown. The previous
  // 'all' option was removed because most downstream queries either don't
  // support a cross-entity view or produce confusing mixed numbers.
  const [entity,          setEntity]          = useState('corporate')
  // Specific corporate or institute id picked from the new dropdown
  // that sits next to Period. Whichever matches the active `entity` is
  // sent to the backend as corporateId / instituteId. 'all' = no scope
  // (show every entity of that type, the previous default).
  const [entityId,        setEntityId]        = useState('all')
  const [entityList,      setEntityList]      = useState([])
  const [typeFilter,      setTypeFilter]      = useState('all')
  const [period,          setPeriod]          = useState(null)
  const [cefr,            setCefr]            = useState('all')
  // Status filter UI was removed — the toggle group lived between
  // Period and the sub-tab bar but the product no longer scopes the
  // dashboard by lifecycle status. State is kept (defaults to 'all')
  // so existing buildParams + dependency lists keep working; when
  // status === 'all' the backend applies no status condition anywhere.
  const [status, setStatus] = useState('all') // eslint-disable-line no-unused-vars
  const [subTab,          setSubTab]          = useState('Overview')
  const [chartTypeFilter, setChartTypeFilter] = useState('all')
  const [tableType,       setTableType]       = useState('all')
  const [tableEntity,     setTableEntity]     = useState('corporate')
  const [rankSearch,      setRankSearch]      = useState('')
  const [rankSegment,     setRankSegment]     = useState('all')
  const [rankPage,        setRankPage]        = useState(1)
  const [topN,            setTopN]            = useState(10)
  const [pvaTrendType,    setPvaTrendType]    = useState('all')
  const [perfViewBy,      setPerfViewBy]      = useState('institute')
  const [perfEntityFilter,setPerfEntityFilter]= useState('Corporate')
  const [perfTypeFilter,  setPerfTypeFilter]  = useState('all')
  const [perfSearch,      setPerfSearch]      = useState('')
  const [perfPage,        setPerfPage]        = useState(1)
  const [growthPeriod,    setGrowthPeriod]    = useState('monthly')
  const [growthTypeFilter,setGrowthTypeFilter]= useState('all')
  const [growthPage,      setGrowthPage]      = useState(1)
  const [growthSearch,    setGrowthSearch]    = useState('')

  const [summary,       setSummary]       = useState(null)
  const [typeBreak,     setTypeBreak]     = useState([])
  // Per-utilisation-table override fetch — kept separate from the page-level
  // `typeBreak` so the table's Entity dropdown can scope without affecting
  // the charts above. Lazy-populated only when tableEntity differs from
  // the page-level entity selection.
  const [utilTypeBreak, setUtilTypeBreak] = useState(null)
  const [loading,       setLoading]       = useState(true)
  const [scoreTypeData, setScoreTypeData] = useState([])
  const [scoreDistData, setScoreDistData] = useState({})
  const [scoreRankData, setScoreRankData] = useState({ data: [], total: 0 })
  const [topCandidates, setTopCandidates] = useState({ corporate: [], institute: [] })
  const [scoresLoading, setScoresLoading] = useState(false)
  const [rankLoading,   setRankLoading]   = useState(false)
  const [topLoading,    setTopLoading]    = useState(false)
  // Practice vs Actual
  const [pvaSummary,    setPvaSummary]    = useState(null)
  const [pvaTrend,      setPvaTrend]      = useState([])
  const [pvaFrequency,  setPvaFrequency]  = useState([])
  const [scheduleUtil,  setScheduleUtil]  = useState([])
  const [pvaLoading,    setPvaLoading]    = useState(false)
  // Student Growth
  const [growthTrend,   setGrowthTrend]   = useState([])
  const [growthEntity,  setGrowthEntity]  = useState([])
  const [growthDonut,   setGrowthDonut]   = useState([])
  const [growthCohort,  setGrowthCohort]  = useState({ data: [], total: 0 })
  const [growthSort,    setGrowthSort]    = useState({ columnKey: undefined, order: undefined })
  const [growthLoading, setGrowthLoading] = useState(false)
  const [cohortLoading, setCohortLoading] = useState(false)
  // Performance
  const [perfBarData,   setPerfBarData]   = useState([])
  const [perfRadar,     setPerfRadar]     = useState([])
  const [perfDetail,    setPerfDetail]    = useState({ data: [], total: 0 })
  const [perfLoading,   setPerfLoading]   = useState(false)
  const [perfTableLoad, setPerfTableLoad] = useState(false)
  // Analytics
  const [analyticsTypeSplit,   setAnalyticsTypeSplit]   = useState([])
  const [analyticsWeekly,      setAnalyticsWeekly]      = useState([])
  const [analyticsCounts,      setAnalyticsCounts]      = useState([])
  const [analyticsLoading,     setAnalyticsLoading]     = useState(false)
  // Predictive
  const [predictiveKPIs,       setPredictiveKPIs]       = useState(null)
  const [predictiveForecast,   setPredictiveForecast]   = useState([])
  const [predictiveAtRisk,     setPredictiveAtRisk]     = useState([])
  const [predictiveAtRiskList, setPredictiveAtRiskList] = useState({ total: 0, data: [] })
  const [predictiveFeatures,   setPredictiveFeatures]   = useState([])
  const [predictiveLoading,    setPredictiveLoading]    = useState(false)
  const [predictiveListPage,   setPredictiveListPage]   = useState(1)
  const [predictiveSearch,     setPredictiveSearch]     = useState('')
  // Cross-Institute
  const [crossScores,          setCrossScores]          = useState([])
  const [crossRadar,           setCrossRadar]           = useState([])
  const [crossPercentile,      setCrossPercentile]      = useState([])
  const [crossTable,           setCrossTable]           = useState([])
  const [crossLoading,         setCrossLoading]         = useState(false)
  const [crossMetric,          setCrossMetric]          = useState('avg_score')
  // Schedule
  const [scheduleChart,        setScheduleChart]        = useState([])
  const [scheduleChartPeriod,  setScheduleChartPeriod]  = useState('monthly')
  const [scheduleLoading,      setScheduleLoading]      = useState(false)
  // Entities
  const [entitiesCharts,       setEntitiesCharts]       = useState({ corporate: [], institute: [] })
  const [entitiesTable,        setEntitiesTable]        = useState([])
  const [entitiesLoading,      setEntitiesLoading]      = useState(false)
  const [entitiesFilter,       setEntitiesFilter]       = useState('corporate')
  const [exportLoading,        setExportLoading]        = useState(false)

  const buildParams = useCallback(() => {
    const p = new URLSearchParams()
    p.set('entity', entity)
    // Single-entity drill — narrow to one corporate or one institute.
    if (entityId !== 'all') {
      if (entity === 'corporate') p.set('corporateId', entityId)
      else if (entity === 'institute') p.set('instituteId', entityId)
    }
    if (typeFilter !== 'all') p.set('type', typeFilter)
    if (typeFilter === 'Communication') p.set('cefr', cefr)
    else if (cefr !== 'all')            p.set('cefr', cefr)
    if (status !== 'all')     p.set('status', status)
    if (period?.[0])          p.set('fromDate', period[0].format('YYYY-MM-DD'))
    if (period?.[1])          p.set('toDate',   period[1].format('YYYY-MM-DD'))
    return p
  }, [entity, entityId, typeFilter, cefr, status, period])

  const load = useCallback(async () => {
    setLoading(true)
    const p = buildParams()
    const [s, t] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/summary?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/type-breakdown?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(s)) setSummary(val(s))
    if (val(t)) setTypeBreak(val(t))
    setLoading(false)
  }, [buildParams])

  useEffect(() => { load() }, [load])

  const loadScoresTab = useCallback(async () => {
    setScoresLoading(true)
    const p = buildParams()
    const [a, d] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/score-type-averages?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/score-distribution?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(a)) setScoreTypeData(val(a))
    if (val(d)) setScoreDistData(val(d))
    setScoresLoading(false)
  }, [buildParams])

  const loadRanking = useCallback(async (page) => {
    setRankLoading(true)
    const p = buildParams()
    if (rankSearch)            p.set('search',  rankSearch)
    if (rankSegment !== 'all') p.set('segment', rankSegment)
    p.set('page',  page || rankPage)
    p.set('limit', 20)
    const res = await adminRequest.get(`/meta-dashboard/assessment/score-ranking?${p}`).catch(() => null)
    const d = res?.data?.data
    if (d) setScoreRankData(d)
    setRankLoading(false)
  }, [buildParams, rankSearch, rankSegment, rankPage])

  const loadTopCandidates = useCallback(async () => {
    setTopLoading(true)
    const p = buildParams()
    p.set('topN', topN)
    const res = await adminRequest.get(`/meta-dashboard/assessment/top-candidates?${p}`).catch(() => null)
    const d = res?.data?.data
    if (d) setTopCandidates(d)
    setTopLoading(false)
  }, [buildParams, topN])

  useEffect(() => {
    if (subTab === 'Scores & Rankings') { loadScoresTab(); setRankPage(1); loadRanking(1) }
    if (subTab === 'Top Candidates')    loadTopCandidates()
  }, [subTab, entity, entityId, typeFilter, cefr, status, period]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Scores & Rankings') loadRanking(rankPage)
  }, [rankPage]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Scores & Rankings') { setRankPage(1); loadRanking(1) }
  }, [rankSearch, rankSegment]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Top Candidates') loadTopCandidates()
  }, [topN]) // eslint-disable-line

  const loadPerformanceCharts = useCallback(async () => {
    setPerfLoading(true)
    const p = buildParams()
    p.set('viewBy', perfViewBy)
    p.set('entityFilter', perfEntityFilter)
    if (perfTypeFilter   !== 'all') p.set('typeFilter',   perfTypeFilter)
    const [bar, radar] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/performance-by-entity?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/performance-radar?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(bar))   setPerfBarData(val(bar))
    if (val(radar)) setPerfRadar(val(radar))
    setPerfLoading(false)
  }, [buildParams, perfViewBy, perfEntityFilter, perfTypeFilter])

  const loadPerformanceTable = useCallback(async (page) => {
    setPerfTableLoad(true)
    const p = buildParams()
    p.set('entityFilter', perfEntityFilter)
    if (perfTypeFilter   !== 'all') p.set('typeFilter',   perfTypeFilter)
    if (perfSearch)                 p.set('search',       perfSearch)
    p.set('page',  page || perfPage)
    p.set('limit', 20)
    const res = await adminRequest.get(`/meta-dashboard/assessment/performance-detail?${p}`).catch(() => null)
    const d = res?.data?.data
    if (d) setPerfDetail(d)
    setPerfTableLoad(false)
  }, [buildParams, perfEntityFilter, perfTypeFilter, perfSearch, perfPage])

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    const p = buildParams()
    const [ts, wa, cn] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/analytics-type-split?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/analytics-weekly?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/analytics-counts?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(ts)) setAnalyticsTypeSplit(val(ts))
    if (val(wa)) setAnalyticsWeekly(val(wa))
    if (val(cn)) setAnalyticsCounts(val(cn))
    setAnalyticsLoading(false)
  }, [buildParams])

  useEffect(() => {
    if (subTab === 'Performance') { loadPerformanceCharts(); setPerfPage(1); loadPerformanceTable(1) }
    if (subTab === 'Analytics')   loadAnalytics()
  }, [subTab, entity, entityId, typeFilter, cefr, status, period]) // eslint-disable-line

  // The Performance sub-tab no longer renders its own filter bar — the
  // view scope follows the page-level Entity dropdown. Keep the existing
  // perfViewBy / perfEntityFilter state so the loaders don't have to be
  // re-engineered; just sync them off `entity` whenever it changes.
  useEffect(() => {
    if (entity === 'corporate') { setPerfViewBy('corporate'); setPerfEntityFilter('Corporate') }
    else if (entity === 'institute') { setPerfViewBy('institute'); setPerfEntityFilter('Institute') }
    setPerfTypeFilter('all')
    setPerfSearch('')
  }, [entity])

  // CEFR is only meaningful for Communication. When the user moves Type
  // away from Communication, clear any previously-set CEFR value so it
  // doesn't keep silently scoping the page (the dropdown is hidden in
  // that state, so a stale value would otherwise be invisible).
  useEffect(() => {
    if (typeFilter !== 'Communication' && cefr !== 'all') setCefr('all')
  }, [typeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // When the page-level Entity flips, refresh the secondary
  // Corporate/Institute dropdown list and reset the picked id back to
  // 'all'. Also bounce off the Practice vs Actual sub-tab on Corporate
  // since that sub-tab is institute-only now.
  useEffect(() => {
    setEntityId('all')
    const url = entity === 'corporate'
      ? '/meta-dashboard/assessment/filters/corporates'
      : '/meta-dashboard/assessment/filters/institutes'
    adminRequest.get(url)
      .then(r => setEntityList(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setEntityList([]))
    if (entity === 'corporate' && subTab === 'Practice vs Actual') {
      setSubTab('Overview')
    }
  }, [entity]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the Assessment-utilisation table in sync with the page-level
  // Entity dropdown. Without this, switching the page Entity from
  // Corporate to Institute (or vice versa) left the table's own Entity
  // selector stuck on its previous value, so the table kept rendering
  // the old entity's rows. Users can still override the table dropdown
  // afterwards; this just resets it whenever the page Entity changes.
  useEffect(() => {
    setTableEntity(entity)
  }, [entity])

  useEffect(() => {
    if (subTab === 'Performance') loadPerformanceCharts()
  }, [perfViewBy, perfEntityFilter, perfTypeFilter]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Performance') { setPerfPage(1); loadPerformanceTable(1) }
  }, [perfEntityFilter, perfTypeFilter, perfSearch]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Performance') loadPerformanceTable(perfPage)
  }, [perfPage]) // eslint-disable-line

  const loadPracticeVsActual = useCallback(async () => {
    setPvaLoading(true)
    const p = buildParams()
    if (pvaTrendType !== 'all') p.set('type', pvaTrendType)
    const [s, tr, fr, su] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/practice-summary?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/practice-trend?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/practice-frequency?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/schedule-utilisation?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(s))  setPvaSummary(val(s))
    if (val(tr)) setPvaTrend(val(tr))
    if (val(fr)) setPvaFrequency(val(fr))
    if (val(su)) setScheduleUtil(val(su))
    setPvaLoading(false)
  }, [buildParams, pvaTrendType])

  const loadStudentGrowth = useCallback(async () => {
    setGrowthLoading(true)
    const p = buildParams()
    p.set('period', growthPeriod)
    if (growthTypeFilter !== 'all') p.set('type', growthTypeFilter)
    const [tr, en, im] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/growth-trend?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/growth-by-entity?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/growth-improvement?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(tr)) setGrowthTrend(val(tr))
    if (val(en)) setGrowthEntity(val(en))
    if (val(im)) setGrowthDonut(val(im))
    setGrowthLoading(false)
  }, [buildParams, growthPeriod, growthTypeFilter])

  const loadGrowthCohort = useCallback(async (page) => {
    setCohortLoading(true)
    const p = buildParams()
    p.set('period', growthPeriod)
    if (growthTypeFilter !== 'all') p.set('type', growthTypeFilter)
    if (growthSearch) p.set('search', growthSearch)
    if (growthSort.columnKey && growthSort.order) {
      p.set('sortBy',    growthSort.columnKey)
      p.set('sortOrder', growthSort.order === 'ascend' ? 'asc' : 'desc')
    }
    p.set('page', page || growthPage)
    p.set('limit', 20)
    const res = await adminRequest.get(`/meta-dashboard/assessment/growth-cohort?${p}`).catch(() => null)
    const d = res?.data?.data
    if (d) setGrowthCohort(d)
    setCohortLoading(false)
  }, [buildParams, growthPeriod, growthTypeFilter, growthSearch, growthSort.columnKey, growthSort.order, growthPage])

  useEffect(() => {
    if (subTab === 'Practice vs Actual') loadPracticeVsActual()
    if (subTab === 'Student Growth')     { loadStudentGrowth(); setGrowthPage(1); loadGrowthCohort(1) }
  }, [subTab, entity, entityId, typeFilter, cefr, status, period]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Practice vs Actual') loadPracticeVsActual()
  }, [pvaTrendType]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Student Growth') { loadStudentGrowth(); setGrowthPage(1); loadGrowthCohort(1) }
  }, [growthPeriod, growthTypeFilter]) // eslint-disable-line

  // Debounce growthSearch so we don't fire an API call on every keystroke.
  useEffect(() => {
    if (subTab !== 'Student Growth') return
    const t = setTimeout(() => { setGrowthPage(1); loadGrowthCohort(1) }, 350)
    return () => clearTimeout(t)
  }, [growthSearch]) // eslint-disable-line

  // Refire when the user clicks a column header — the table sort state is
  // server-side, so changing it requires a new fetch.
  useEffect(() => {
    if (subTab !== 'Student Growth') return
    setGrowthPage(1); loadGrowthCohort(1)
  }, [growthSort.columnKey, growthSort.order]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Student Growth') loadGrowthCohort(growthPage)
  }, [growthPage]) // eslint-disable-line

  const loadPredictive = useCallback(async () => {
    setPredictiveLoading(true)
    const p = buildParams()
    const [k, f, ar, feat] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/predictive-kpis?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/predictive-forecast?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/predictive-at-risk?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/predictive-features?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(k))    setPredictiveKPIs(val(k))
    if (val(f))    setPredictiveForecast(val(f))
    if (val(ar))   setPredictiveAtRisk(val(ar))
    if (val(feat)) setPredictiveFeatures(val(feat))
    setPredictiveLoading(false)
  }, [buildParams])

  const loadPredictiveList = useCallback(async (page) => {
    const p = buildParams()
    if (predictiveSearch) p.set('search', predictiveSearch)
    p.set('page', page || predictiveListPage)
    p.set('limit', 20)
    const res = await adminRequest.get(`/meta-dashboard/assessment/predictive-at-risk-list?${p}`).catch(() => null)
    const d = res?.data?.data
    if (d) setPredictiveAtRiskList(d)
  }, [buildParams, predictiveListPage, predictiveSearch])

  useEffect(() => {
    if (subTab === 'Predictive') { loadPredictive(); setPredictiveListPage(1); loadPredictiveList(1) }
  }, [subTab, entity, entityId, typeFilter, cefr, status, period]) // eslint-disable-line

  useEffect(() => {
    if (subTab === 'Predictive') loadPredictiveList(predictiveListPage)
  }, [predictiveListPage]) // eslint-disable-line

  // Debounce search on the at-risk list.
  useEffect(() => {
    if (subTab !== 'Predictive') return
    const t = setTimeout(() => { setPredictiveListPage(1); loadPredictiveList(1) }, 350)
    return () => clearTimeout(t)
  }, [predictiveSearch]) // eslint-disable-line

  const loadCrossInstitute = useCallback(async () => {
    setCrossLoading(true)
    const p = buildParams()
    p.set('metric', crossMetric)
    const [sc, rd, pct, tbl] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/cross-institute-scores?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/cross-institute-radar?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/cross-institute-percentile?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/cross-institute-table?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(sc))  setCrossScores(val(sc))
    if (val(rd))  setCrossRadar(val(rd))
    if (val(pct)) setCrossPercentile(val(pct))
    if (val(tbl)) setCrossTable(val(tbl))
    setCrossLoading(false)
  }, [buildParams, crossMetric])

  useEffect(() => {
    if (subTab === 'Cross-Institute') loadCrossInstitute()
  }, [subTab, entity, entityId, typeFilter, cefr, status, period, crossMetric]) // eslint-disable-line

  const loadSchedule = useCallback(async () => {
    setScheduleLoading(true)
    const p = buildParams()
    p.set('period', scheduleChartPeriod)
    const base = buildParams()
    const [chartRes, utilRes] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/schedule-chart?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/schedule-utilisation?${base}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(chartRes)) setScheduleChart(val(chartRes))
    if (val(utilRes))  setScheduleUtil(val(utilRes))
    setScheduleLoading(false)
  }, [buildParams, scheduleChartPeriod])

  useEffect(() => {
    if (subTab === 'Schedule') { loadSchedule() }
  }, [subTab, entity, entityId, typeFilter, cefr, status, period, scheduleChartPeriod]) // eslint-disable-line

  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true)
    const p = buildParams()
    p.set('entityFilter', entitiesFilter)
    const [ch, tbl] = await Promise.allSettled([
      adminRequest.get(`/meta-dashboard/assessment/entities-charts?${p}`),
      adminRequest.get(`/meta-dashboard/assessment/entities-table?${p}`),
    ])
    const val = r => r.status === 'fulfilled' ? r.value?.data?.data : null
    if (val(ch))  setEntitiesCharts(val(ch))
    if (val(tbl)) setEntitiesTable(val(tbl))
    setEntitiesLoading(false)
  }, [buildParams, entitiesFilter])

  useEffect(() => {
    if (subTab === 'Entities') loadEntities()
  }, [subTab, entity, entityId, typeFilter, cefr, status, period, entitiesFilter]) // eslint-disable-line

  // Entity-aware Type filter options. typeBreak is fetched from
  // /assessment/type-breakdown under the current entity / period / cefr /
  // status filters; intersect with the canonical TYPE_KEYS to keep order
  // stable. Falls back to TYPE_KEYS while typeBreak is loading so the
  // pills don't briefly collapse to just "All Types".
  const availableTypes = useMemo(() => {
    if (!Array.isArray(typeBreak) || typeBreak.length === 0) return TYPE_KEYS
    const present = new Set(
      typeBreak
        .filter(r => (r?.assigned || 0) > 0 || (r?.submitted || 0) > 0)
        .map(r => r?.type_name)
    )
    const filtered = TYPE_KEYS.filter(k => present.has(k))
    return filtered.length ? filtered : TYPE_KEYS
  }, [typeBreak])

  // If the user had a Type filter selected and switching entity drops that
  // type from availableTypes, fall back to "all" so the dashboard doesn't
  // sit on an empty-data filter the user can't see in the pill row anymore.
  useEffect(() => {
    if (availableTypes === TYPE_KEYS) return
    if (typeFilter        !== 'all' && !availableTypes.includes(typeFilter))        setTypeFilter('all')
    if (chartTypeFilter   !== 'all' && !availableTypes.includes(chartTypeFilter))   setChartTypeFilter('all')
    if (tableType         !== 'all' && !availableTypes.includes(tableType))         setTableType('all')
    if (pvaTrendType      !== 'all' && !availableTypes.includes(pvaTrendType))      setPvaTrendType('all')
    if (growthTypeFilter  !== 'all' && !availableTypes.includes(growthTypeFilter))  setGrowthTypeFilter('all')
  }, [availableTypes]) // eslint-disable-line

  // Computed totals
  const totalGenerated = useMemo(
    () => typeBreak.reduce((s, r) => s + (r.assigned  || 0), 0),
    [typeBreak],
  )
  const totalUsed = useMemo(
    () => typeBreak.reduce((s, r) => s + (r.submitted || 0), 0),
    [typeBreak],
  )
  const utilisationRate = useMemo(
    () => totalGenerated > 0 ? ((totalUsed / totalGenerated) * 100).toFixed(1) : null,
    [totalGenerated, totalUsed],
  )

  // ── KPI definitions ─────────────────────────────────────────
  // Every KPI here is scoped to the active filter bar — backend's
  // /summary endpoint applies entity/type/status/date/cefr filters
  // before aggregating. Labels reflect what's *actually* being counted
  // so the numbers reconcile with the rest of the Assessment tab.
  const kpis = useMemo(() => [
    {
      icon: <CheckCircleOutlined />, color: '#3249D7',
      label: 'Completed (assignments)',
      value: fmt(summary?.total_submitted),
      trend: summary ? `↑ ${summary.completion_rate}% completion rate` : null,
      trendColor: '#22c55e',
    },
    {
      icon: <ThunderboltOutlined />, color: '#f59e0b',
      label: 'Active (in-flight)',
      value: fmt(summary?.active_now),
      trend: summary ? `● Unsubmitted, window not ended` : null,
      trendColor: '#7b8499',
    },
    {
      icon: <TeamOutlined />, color: '#0891b2',
      label: 'Assigned Candidates',
      value: fmt(summary?.total_candidates),
      trend: summary?.assessed_candidates != null
        ? `● ${fmt(summary.assessed_candidates)} assessed`
        : null,
      trendColor: '#22c55e',
    },
    {
      icon: <BarChartOutlined />, color: '#6d28d9',
      label: 'Avg Communication Score',
      value: summary?.avg_score != null ? `${summary.avg_score}` : '—',
      trend: summary?.avg_score != null ? '● Submitted Communication only' : '— No score data yet',
      trendColor: summary?.avg_score != null ? scoreColor(summary.avg_score) : '#7b8499',
    },
    // Renewals Due KPI temporarily hidden — to be re-enabled later.
    // Backend still returns `summary.renewals_due`, so uncommenting this
    // block restores the card without any further changes.
    /*
    {
      icon: <AlertOutlined />, color: '#e65100',
      label: 'Renewals Due',
      value: summary ? fmt(summary.renewals_due) : '—',
      trend: summary ? `● Due in next 30 days (in scope)` : null,
      trendColor: summary?.renewals_due > 0 ? '#e65100' : '#22c55e',
    },
    */
    {
      icon: <PieChartOutlined />, color: '#0891b2',
      label: 'Utilisation Rate',
      value: utilisationRate ? `${utilisationRate}%` : '—',
      trend: utilisationRate ? `${totalUsed.toLocaleString()} of ${totalGenerated.toLocaleString()} used` : null,
      trendColor: '#22c55e',
    },
    // Active-subscriptions card — switches label + value based on the
    // page-level Entity. Institute view shows the active-subscribed
    // colleges count (matches "Active Sub Colleges" elsewhere); Corporate
    // view shows active-subscribed companies (matches "Active Sub
    // Companies"). Backend returns both fields on every summary call.
    {
      icon: <BankOutlined />, color: '#e65100',
      label: entity === 'corporate' ? 'Corporates (in scope)' : 'Institutes (in scope)',
      value: fmt(entity === 'corporate'
        ? summary?.subscribed_corporates
        : summary?.subscribed_institutes),
      trend: summary
        ? (entity === 'corporate'
            ? '● Active subscribed companies'
            : '● Active subscribed institutes')
        : null,
      trendColor: '#7b8499',
    },
    {
      icon: <PlayCircleOutlined />, color: '#10b981',
      label: 'AI Interviews',
      value: fmt(summary?.ai_interviews),
      trend: summary ? '● Sessions in scope' : null,
      trendColor: '#7b8499',
    },
  ], [summary, totalGenerated, totalUsed, utilisationRate, entity])

  // ── Donut chart data ─────────────────────────────────────────
  const donutData = useMemo(
    () => typeBreak.map(r => ({ name: r.type_name || 'Unknown', value: r.assigned || 0 })),
    [typeBreak],
  )

  // ── Generated vs Used bar chart ──────────────────────────────
  const barData = useMemo(() => {
    const rows = typeBreak.map(r => ({
      name:      r.type_name || 'Unknown',
      Generated: r.assigned  || 0,
      Used:      r.submitted || 0,
    }))
    return chartTypeFilter === 'all' ? rows : rows.filter(d => d.name === chartTypeFilter)
  }, [typeBreak, chartTypeFilter])

  // The effective entity to filter the utilisation table by. The table's
  // dropdown overrides the page-level entity filter when set to anything
  // other than 'all'.
  const effectiveTableEntity = tableEntity !== 'all' ? tableEntity : entity

  // Refetch type-breakdown scoped to the table's effective entity when it
  // differs from what the page-level `load` already fetched. When the two
  // match we reuse `typeBreak` directly to avoid a redundant request.
  useEffect(() => {
    if (tableEntity === 'all') { setUtilTypeBreak(null); return }
    if (tableEntity === entity) { setUtilTypeBreak(null); return }
    let cancelled = false
    const p = new URLSearchParams()
    p.set('entity', tableEntity)
    if (typeFilter !== 'all') p.set('type', typeFilter)
    // Always send `cefr` when Type=Communication (including 'all') so the
    // backend can distinguish "All Levels" (= require any CEFR, makes the
    // per-level Completed counts tally) from "no CEFR filter at all".
    // For other types CEFR is meaningless and intentionally omitted.
    if (typeFilter === 'Communication') p.set('cefr', cefr)
    else if (cefr !== 'all')            p.set('cefr', cefr)
    if (status !== 'all')     p.set('status', status)
    if (period?.[0])          p.set('fromDate', period[0].format('YYYY-MM-DD'))
    if (period?.[1])          p.set('toDate',   period[1].format('YYYY-MM-DD'))
    adminRequest.get(`/meta-dashboard/assessment/type-breakdown?${p}`)
      .then(r => { if (!cancelled) setUtilTypeBreak(r?.data?.data || []) })
      .catch(() => { if (!cancelled) setUtilTypeBreak([]) })
    return () => { cancelled = true }
  }, [tableEntity, entity, typeFilter, cefr, status, period])

  const entityLabel = (v) => v === 'corporate' ? 'Corporate' : v === 'institute' ? 'Institute' : 'All'

  // ── Utilisation table rows ────────────────────────────────────
  const utilisationRows = useMemo(() => {
    const src = utilTypeBreak != null ? utilTypeBreak : typeBreak
    return src
      .filter(r => tableType === 'all' || r.type_name === tableType)
      .map((r, i) => {
        const gen   = r.assigned  || 0
        const used  = r.submitted || 0
        const pct   = gen > 0 ? Math.round((used / gen) * 100) : 0
        return { key: i, type_name: r.type_name || 'Unknown', entity: entityLabel(effectiveTableEntity), gen, used, unused: gen - used, pct, avg_score: r.avg_score ?? null }
      })
  }, [typeBreak, utilTypeBreak, tableType, effectiveTableEntity])

  // ── Analytics donut data (per-chart Hinglish handling) ─────────
  // Hinglish is treated as a Communication sub-variant by the product:
  //  - Corporate type split + Completion rates: merge Hinglish counts
  //    into Communication (and recompute completion_rate accordingly).
  //  - Institute type split: omit Hinglish entirely.
  const _mergeHinglishInto = (rows, countKeys, recomputeRate = false) => {
    const out = []
    let comm = null
    for (const r of rows) {
      if (r.type_name === 'Hinglish') {
        if (!comm) {
          comm = { ...r, type_name: 'Communication' }
          out.push(comm)
        } else {
          countKeys.forEach(k => { comm[k] = (comm[k] || 0) + (r[k] || 0) })
        }
      } else if (r.type_name === 'Communication') {
        const merged = { ...r }
        if (comm) {
          countKeys.forEach(k => { merged[k] = (merged[k] || 0) + (comm[k] || 0) })
          // Replace the placeholder we pushed when Hinglish came first.
          const idx = out.indexOf(comm)
          if (idx !== -1) out.splice(idx, 1)
        }
        comm = merged
        out.push(merged)
      } else {
        out.push({ ...r })
      }
    }
    if (recomputeRate) {
      out.forEach(r => {
        r.completion_rate = r.total_count > 0 ? Math.round((r.submitted_count / r.total_count) * 100) : 0
      })
    }
    return out
  }
  const analyticsCorpData = useMemo(
    () => _mergeHinglishInto(analyticsTypeSplit, ['corporate_count']),
    [analyticsTypeSplit]
  )
  const analyticsInstData = useMemo(
    () => analyticsTypeSplit.filter(d => d.type_name !== 'Hinglish'),
    [analyticsTypeSplit]
  )
  const analyticsCompletionData = useMemo(
    () => _mergeHinglishInto(analyticsTypeSplit, ['total_count', 'submitted_count'], true),
    [analyticsTypeSplit]
  )

  // ── Utilisation table columns ─────────────────────────────────
  const utilisationColumns = [
    {
      title: 'ASSESSMENT TYPE', dataIndex: 'type_name', key: 'type',
      render: v => {
        const cfg = typeCfg(v)
        return <Tag color={cfg.tag} style={{ fontWeight: 600, fontSize: 12, borderRadius: 6 }}>{v}</Tag>
      },
    },
    {
      title: 'ENTITY', dataIndex: 'entity', key: 'entity',
      render: v => <Tag style={{ fontSize: 12, borderRadius: 6 }}>{v || 'All'}</Tag>,
    },
    {
      title: 'GENERATED', dataIndex: 'gen', key: 'gen', align: 'center',
      render: v => <span style={{ fontWeight: 600 }}>{fmt(v)}</span>,
      sorter: (a, b) => a.gen - b.gen,
    },
    {
      title: 'USED', dataIndex: 'used', key: 'used', align: 'center',
      render: v => <span style={{ fontWeight: 600 }}>{fmt(v)}</span>,
      sorter: (a, b) => a.used - b.used,
    },
    {
      title: 'UNUSED', dataIndex: 'unused', key: 'unused', align: 'center',
      render: v => <span style={{ color: '#888' }}>{fmt(v)}</span>,
    },
    {
      title: 'AVG SCORE', dataIndex: 'avg_score', key: 'score',
      sorter: (a, b) => (a.avg_score ?? -1) - (b.avg_score ?? -1),
      render: v => v != null ? <span style={{ fontWeight: 600, color: scoreColor(v) }}>{v}</span> : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'COMPLETION %', dataIndex: 'pct', key: 'comp',
      render: v => <span style={{ fontWeight: 600, color: utilColor(v) }}>{v}%</span>,
      sorter: (a, b) => a.pct - b.pct,
    },
    {
      title: 'UTILISATION', dataIndex: 'pct', key: 'util',
      render: v => <InlineBar numVal={v} displayVal={`${v}%`} color={utilColor(v)} />,
      sorter: (a, b) => a.pct - b.pct,
    },
    {
      title: 'STATUS', key: 'status',
      render: () => <Tag color="green" style={{ fontWeight: 600, fontSize: 11, borderRadius: 6 }}>Active</Tag>,
    },
  ]

  // ── Export / Print ───────────────────────────────────────────
  const ENTITIES_COLS = [
    { h: 'Entity',          k: 'entity_name' },
    { h: 'Type',            k: 'entity_type' },
    { h: 'State',           k: 'state' },
    // 'Total' is the row-level assessment count, 'Candidates' is the
    // distinct student count for that entity — these used to be conflated
    // (Total held the candidate count) until the entities-table endpoint
    // was rewritten to expose both as separate fields.
    { h: 'Total',           k: 'total_assessments' },
    { h: 'Candidates',      k: 'candidate_count' },
    { h: 'Comm Count',      k: 'comm_count' },
    { h: 'Comm Avg',        k: 'comm_avg' },
    { h: 'Apt Count',       k: 'apt_count' },
    { h: 'Apt Avg',         k: 'apt_avg' },
    { h: 'Psy Count',       k: 'psy_count' },
    { h: 'Psy Avg',         k: 'psy_avg' },
    { h: 'AI_Interview Count',    k: 'ai_count' },
    { h: 'AI_Interview Avg',      k: 'ai_avg' },
    { h: 'Custom_Assessment Count', k: 'custom_count' },
    { h: 'Custom_Assessment Avg',   k: 'custom_avg' },
    { h: 'Avg Score',       k: 'avg_score' },
    { h: 'Utilisation %',   k: 'utilisation_pct' },
    { h: 'Status',          k: 'status' },
  ]

  const handleExportExcel = async () => {
    setExportLoading(true)
    try {
      const p = buildParams()
      const res = await adminRequest.get(`/meta-dashboard/assessment/entities-table?${p}`)
      const rows = res?.data?.data || []
      const date = new Date().toISOString().slice(0, 10)
      exportXlsx(rows, ENTITIES_COLS, `assessment_360_entities_${date}.xlsx`)
    } finally {
      setExportLoading(false)
    }
  }

  const handlePrint = () => {
    const el = document.getElementById('assessment360-print')
    if (!el) { window.print(); return }
    const style = document.createElement('style')
    style.id = '__a360-print-style'
    style.innerHTML = `
      @media print {
        body > * { display: none !important; }
        #assessment360-print,
        #assessment360-print * { display: revert !important; }
        #assessment360-print .no-print { display: none !important; }
      }
    `
    document.head.appendChild(style)
    window.print()
    document.head.removeChild(style)
  }

  if (loading) return <SpinWrap><Spin size="large" /></SpinWrap>

  return (
    <div id="assessment360-print">
      {/* Inner header */}
      <InnerHeader>
        <InnerTitle>
          Assessment Analytics Dashboard
          <Tooltip title="Subscriptions expiring within 30 days">
            <AlertBadge>
              <AlertOutlined /> renewals due
            </AlertBadge>
          </Tooltip>
        </InnerTitle>
        <ActionBtns className="no-print">
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={handleExportExcel}
          >
            Export Excel
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            style={{ background: '#1a1a2e', borderColor: '#1a1a2e' }}
            onClick={handlePrint}
          >
            Print
          </Button>
        </ActionBtns>
      </InnerHeader>

      {/* Filter bar */}
      <FilterBar>
        <FilterLabel>Entity</FilterLabel>
        <Select value={entity} onChange={setEntity} size="small" style={{ width: 130 }} getPopupContainer={t => t.parentNode}>
          <Option value="corporate">Corporate</Option>
          <Option value="institute">Institute</Option>
        </Select>

        <FilterDivider />

        <FilterLabel>Type</FilterLabel>
        <Select value={typeFilter} onChange={setTypeFilter} size="small" style={{ width: 130 }} getPopupContainer={t => t.parentNode}>
          <Option value="all">All Types</Option>
          {availableTypes.map(k => <Option key={k} value={k}>{k}</Option>)}
        </Select>

        <FilterDivider />

        <FilterLabel>Period</FilterLabel>
        <RangePicker size="small" value={period} onChange={setPeriod} format="DD-MM-YYYY" style={{ width: 220 }} />

        {/* Single-entity drill dropdown temporarily commented out per
            product. State (`entityId`, `entityList`) is left in place so
            buildParams + the sub-tab useEffect deps keep working; with no
            UI to change it, entityId stays at 'all' → no corporateId /
            instituteId is ever sent → backend runs unscoped (pre-feature
            behaviour). Uncomment this block to re-enable.
        <FilterDivider />
        <FilterLabel>{entity === 'corporate' ? 'Corporate' : 'Institute'}</FilterLabel>
        <Select
          value={entityId}
          onChange={setEntityId}
          size="small"
          showSearch
          style={{ width: 220 }}
          getPopupContainer={t => t.parentNode}
          optionFilterProp="children"
          filterOption={(input, option) =>
            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
          }
        >
          <Option value="all">{entity === 'corporate' ? 'All Corporates' : 'All Institutes'}</Option>
          {entityList.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
        </Select>
        */}

        {/* CEFR only applies to Communication — the level is meaningless for
            Aptitude / Role_Based / AI_Interview / Custom_Assessment, so we
            hide the dropdown entirely when those types are selected. The
            effect below also resets cefr→'all' when the user moves Type
            away from Communication, so a stale CEFR can't silently filter
            other types. */}
        {typeFilter === 'Communication' && (
          <>
            <FilterDivider />

            <FilterLabel>CEFR</FilterLabel>
            <Select value={cefr} onChange={setCefr} size="small" style={{ width: 110 }} getPopupContainer={t => t.parentNode}>
              <Option value="all">All Levels</Option>
              {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => <Option key={l} value={l}>{l}</Option>)}
            </Select>
          </>
        )}

      </FilterBar>

      {/* Sub-tab bar */}
      <SubTabBar>
        {subTabsFor(entity).map((t, i) => (
          <SubTab key={t} $active={subTab === t} onClick={() => setSubTab(t)}>
            {i === 0 && <TableOutlined style={{ fontSize: 12 }} />}
            {t}
          </SubTab>
        ))}
      </SubTabBar>

      {/* ── Overview ── */}
      {subTab === 'Overview' && (
        <>
          {/* KPI strip */}
          <KPIRow>
            {kpis.map((k, i) => (
              <KPICard key={i}>
                <KPIHead>
                  <KPILabel>{k.label}</KPILabel>
                  <KPIIconWrap $color={k.color}>{k.icon}</KPIIconWrap>
                </KPIHead>
                <KPIValue>{k.value}</KPIValue>
                {k.trend && <Tooltip title={k.trend}><KPITrend $color={k.trendColor}>{k.trend}</KPITrend></Tooltip>}
              </KPICard>
            ))}
          </KPIRow>

          {/* Charts row */}
          <ChartsRow>
            {/* Donut — Assessment type distribution */}
            <ChartCard>
              <ChartCardHeader>
                <ChartCardTitle>
                  <PieChartOutlined /> Assessment type distribution
                </ChartCardTitle>
              </ChartCardHeader>
              <DonutLegend>
                {donutData.map(d => (
                  <LegendItem key={d.name}>
                    <LegendSquare $color={typeCfg(d.name).chart} />
                    {d.name} {d.value.toLocaleString()}
                  </LegendItem>
                ))}
              </DonutLegend>
              {donutData.length === 0
                ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                : (
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={68}
                        outerRadius={108}
                        paddingAngle={2}
                      >
                        {donutData.map((d, i) => (
                          <Cell key={i} fill={typeCfg(d.name).chart} />
                        ))}
                      </Pie>
                      <RTooltip formatter={(v, n) => [v.toLocaleString(), n]} />
                    </PieChart>
                  </ResponsiveContainer>
                )
              }
            </ChartCard>

            {/* Grouped bar — Generated vs Used by type */}
            <ChartCard>
              <ChartCardHeader>
                <ChartCardTitle>
                  <BarChartOutlined /> Generated vs used by type
                </ChartCardTitle>
                <Select
                  value={chartTypeFilter}
                  onChange={setChartTypeFilter}
                  size="small"
                  style={{ width: 110 }}
                  getPopupContainer={t => t.parentNode}
                >
                  <Option value="all">All</Option>
                  {availableTypes.map(k => <Option key={k} value={k}>{k}</Option>)}
                </Select>
              </ChartCardHeader>
              {barData.length === 0
                ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={barData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip />
                      <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        formatter={v => <span style={{ fontWeight: 600 }}>{v}</span>}
                      />
                      <Bar dataKey="Generated" fill="#1e3a8a" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Used"      fill="#166534" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </ChartCard>
          </ChartsRow>

          {/* Assessment utilisation table */}
          <TableSection>
            <TableSectionHeader>
              <TableSectionTitle>
                <TableOutlined /> Assessment utilisation
              </TableSectionTitle>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select value={tableType} onChange={setTableType} size="small" style={{ width: 120 }} getPopupContainer={t => t.parentNode}>
                  <Option value="all">All Types</Option>
                  {availableTypes.map(k => <Option key={k} value={k}>{k}</Option>)}
                </Select>
                {/* Table-level Entity dropdown removed — the page-level
                    Entity filter on the FilterBar drives this table now,
                    so the secondary control was redundant. tableEntity
                    state still exists (auto-synced to `entity` via a
                    useEffect above) so utilisationRows + the override-
                    fetch effect keep working without further changes. */}
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => exportXlsx(utilisationRows, [
                    { h: 'Assessment Type', k: 'type_name' },
                    { h: 'Generated',       k: 'gen' },
                    { h: 'Used',            k: 'used' },
                    { h: 'Unused',          k: 'unused' },
                    { h: 'Completion %',    k: 'pct' },
                  ], 'assessment_utilisation.xlsx')}
                >Export</Button>
              </div>
            </TableSectionHeader>
            <Table
              dataSource={utilisationRows}
              columns={utilisationColumns}
              size="small"
              pagination={false}
              scroll={{ x: 880 }}
              rowKey="key"
            />
          </TableSection>
        </>
      )}

      {/* ── Scores & Rankings ── */}
      {subTab === 'Scores & Rankings' && (
        scoresLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            <ScoreTypeGrid>
              {scoreTypeData.map(d => (
                <ScoreTypeCard key={d.type_name} $color={typeCfg(d.type_name).chart}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Tag color={typeCfg(d.type_name).tag} style={{ fontWeight: 700, fontSize: 12, borderRadius: 6, margin: 0 }}>{d.type_name}</Tag>
                    {/* Lead with assessment count so the card reconciles with
                        Overview > Completed (assignments) — the user
                        comparison point. Show distinct student count after
                        when it differs from the assessment count. */}
                    <span style={{ fontSize: 10, color: '#7b8499' }}>
                      {d.assignment_count != null
                        ? `${d.assignment_count.toLocaleString()} assessments`
                        : `${(d.student_count ?? 0).toLocaleString()} students`}
                      {d.assignment_count != null
                        && d.student_count != null
                        && d.assignment_count !== d.student_count
                        ? ` · ${d.student_count.toLocaleString()} students`
                        : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: typeCfg(d.type_name).color, lineHeight: 1 }}>
                    {d.overall_avg ?? '—'}
                  </div>
                  <div style={{ fontSize: 10, color: '#7b8499', marginTop: 4, marginBottom: 8 }}>Overall avg</div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>{d.corporate_avg ?? '—'}</div>
                      <div style={{ fontSize: 10, color: '#7b8499' }}>Corporate</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>{d.institute_avg ?? '—'}</div>
                      <div style={{ fontSize: 10, color: '#7b8499' }}>Institute</div>
                    </div>
                  </div>
                </ScoreTypeCard>
              ))}
              {scoreTypeData.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', color: '#bbb' }}>No score data yet</div>
              )}
            </ScoreTypeGrid>

            <ChartsRow>
              <ChartCard>
                <ChartCardHeader>
                  {/* Chart title + bar set follow the page-level Entity.
                      The opposite-entity bar would otherwise render as 0
                      everywhere (the page is scoped to one entity), so we
                      show only the relevant series. */}
                  <ChartCardTitle>
                    <BarChartOutlined />{' '}
                    {entity === 'corporate' ? 'Corporate avg by type' : 'Institute avg by type'}
                  </ChartCardTitle>
                </ChartCardHeader>
                {scoreTypeData.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={scoreTypeData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type_name" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <RTooltip formatter={v => v != null ? v : '—'} />
                        <Legend wrapperStyle={{ fontSize: 12 }} formatter={v => <span style={{ fontWeight: 600 }}>{v}</span>} />
                        {entity === 'corporate'
                          ? <Bar dataKey="corporate_avg" name="Corporate" fill="#1e3a8a" radius={[3, 3, 0, 0]} />
                          : <Bar dataKey="institute_avg" name="Institute" fill="#6d28d9" radius={[3, 3, 0, 0]} />}
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Score distribution by band</ChartCardTitle>
                </ChartCardHeader>
                {Object.keys(scoreDistData).length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (() => {
                    const BANDS = ['0–20', '21–40', '41–60', '61–75', '76–90', '91–100']
                    // Keys here mirror the backend's slugified type_name (lower,
                    // non-alphas → '_'). Order controls bar order in the chart.
                    const TYPE_MAP = {
                      communication:     'Communication',
                      aptitude:          'Aptitude',
                      psychometric:      'Role_Based',
                      ai_interview:      'AI_Interview',
                      custom_assessment: 'Custom_Assessment',
                    }
                    const chartData = BANDS.map((label, i) => {
                      const row = { label }
                      Object.entries(scoreDistData).forEach(([k, arr]) => {
                        row[TYPE_MAP[k] || k] = arr[i]?.count || 0
                      })
                      return row
                    })
                    return (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <RTooltip />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          {Object.values(TYPE_MAP).map(t => (
                            <Bar key={t} dataKey={t} fill={typeCfg(t).chart} radius={[2, 2, 0, 0]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    )
                  })()
                }
              </ChartCard>
            </ChartsRow>

            <TableSection>
              <TableSectionHeader>
                <TableSectionTitle><TableOutlined /> Student Rankings</TableSectionTitle>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    placeholder="Search name / email…"
                    value={rankSearch}
                    onChange={e => setRankSearch(e.target.value)}
                    style={{ padding: '3px 10px', border: '1px solid #e8ecf4', borderRadius: 8, fontSize: 13, outline: 'none', width: 180 }}
                  />
                  <StatusToggleGroup>
                    {[['all', 'All'], ['top25', 'Top 25%'], ['bottom25', 'Bottom 25%'], ['needs_attention', 'Needs Attn']].map(([v, l]) => (
                      <StatusToggle key={v} $active={rankSegment === v} onClick={() => setRankSegment(v)}>{l}</StatusToggle>
                    ))}
                  </StatusToggleGroup>
                </div>
              </TableSectionHeader>
              <Table
                dataSource={scoreRankData.data || []}
                rowKey="primary_email"
                size="small"
                loading={rankLoading}
                scroll={{ x: 1000 }}
                pagination={{
                  current: rankPage,
                  pageSize: 20,
                  total: scoreRankData.total || 0,
                  onChange: p => setRankPage(p),
                  showSizeChanger: false,
                  showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}`,
                }}
                columns={[
                  {
                    title: 'STUDENT', dataIndex: 'full_name', key: 'name',
                    render: (v, r) => (
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                        {/* Latest assessment / map name the student took
                            in the current scope. Email moved out to its
                            own EMAIL column below. */}
                        <div style={{ fontSize: 11, color: '#7b8499' }}>{r.entity_name}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'EMAIL', dataIndex: 'primary_email', key: 'email',
                    render: v => v
                      ? <span style={{ fontSize: 12, color: '#1a1a2e', wordBreak: 'break-all' }}>{v}</span>
                      : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'COMM', dataIndex: 'comm_score', key: 'comm', align: 'center', width: 78,
                    render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={typeCfg('Communication').chart} /> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'APT', dataIndex: 'apt_score', key: 'apt', align: 'center', width: 78,
                    render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={typeCfg('Aptitude').chart} /> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'PSY', dataIndex: 'psy_score', key: 'psy', align: 'center', width: 78,
                    render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={typeCfg('Role_Based').chart} /> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'AI INTERVIEW', dataIndex: 'ai_score', key: 'ai', align: 'center', width: 95,
                    render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={typeCfg('AI_Interview').chart} /> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'CUSTOM', dataIndex: 'custom_score', key: 'custom', align: 'center', width: 90,
                    render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={typeCfg('Custom_Assessment').chart} /> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    title: 'OVERALL', dataIndex: 'overall_avg', key: 'overall', align: 'center', width: 82,
                    render: v => <span style={{ fontWeight: 700, fontSize: 14, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span>,
                  },
                  {
                    title: 'GRADE', dataIndex: 'grade', key: 'grade', align: 'center', width: 70,
                    render: v => v && v !== '—' ? <Tag color={gradeTagColor(v)} style={{ fontWeight: 700, borderRadius: 6, margin: 0 }}>{v}</Tag> : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  {
                    // Was "PERCENTILE" — replaced with the explicit rank
                    // position (1, 2, 3 …) the backend already returns,
                    // which is what users actually want here. Rank
                    // ascends naturally with the existing overall-desc
                    // sort, so #1 sits at the top of the table.
                    title: 'RANK', dataIndex: 'rank', key: 'rank', align: 'center', width: 70,
                    render: v => <span style={{ fontWeight: 700, color: '#3249D7' }}>{v != null ? `#${v}` : '—'}</span>,
                  },
                ]}
              />
            </TableSection>
          </>
        )
      )}

      {/* ── Top Candidates ── */}
      {subTab === 'Top Candidates' && (
        topLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: '#7b8499', fontWeight: 500 }}>Show top</span>
              <Select value={topN} onChange={setTopN} size="small" style={{ width: 80 }} getPopupContainer={t => t.parentNode}>
                {[5, 10, 20, 50].map(n => <Option key={n} value={n}>Top {n}</Option>)}
              </Select>
              <span style={{ fontSize: 13, color: '#7b8499' }}>candidates per entity</span>
            </div>

            <SplitGrid>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><ApartmentOutlined /> Top {topN} Corporate Candidates</ChartCardTitle>
                  <Tag color="blue" style={{ fontWeight: 600 }}>{topCandidates.corporate?.length || 0} shown</Tag>
                </ChartCardHeader>
                <CandidateList>
                  {(topCandidates.corporate || []).length === 0
                    ? <div style={{ textAlign: 'center', color: '#bbb', padding: '32px 0' }}>No corporate candidates</div>
                    : (topCandidates.corporate || []).map((c, i) => (
                      <CandidateRow key={c.primary_email}>
                        <RankBadge $rank={i}>{c.rank}</RankBadge>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.full_name}</div>
                              <div style={{ fontSize: 11, color: '#7b8499' }}>{c.entity_name}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 8 }}>
                              <Tag color={gradeTagColor(c.grade)} style={{ fontWeight: 700, fontSize: 12, borderRadius: 6, margin: 0 }}>{c.grade}</Tag>
                              <span style={{ fontSize: 11, color: '#7b8499', whiteSpace: 'nowrap' }}>{c.percentile_label} pct.</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 6, background: '#f0f2f5', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(c.overall_avg || 0, 100)}%`, height: '100%', background: scoreColor(c.overall_avg || 0), borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', minWidth: 30 }}>{c.overall_avg ?? '—'}</span>
                          </div>
                        </div>
                      </CandidateRow>
                    ))
                  }
                </CandidateList>
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BankOutlined /> Top {topN} Institute Candidates</ChartCardTitle>
                  <Tag color="purple" style={{ fontWeight: 600 }}>{topCandidates.institute?.length || 0} shown</Tag>
                </ChartCardHeader>
                <CandidateList>
                  {(topCandidates.institute || []).length === 0
                    ? <div style={{ textAlign: 'center', color: '#bbb', padding: '32px 0' }}>No institute candidates</div>
                    : (topCandidates.institute || []).map((c, i) => (
                      <CandidateRow key={c.primary_email}>
                        <RankBadge $rank={i}>{c.rank}</RankBadge>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.full_name}</div>
                              <div style={{ fontSize: 11, color: '#7b8499' }}>{c.entity_name}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 8 }}>
                              <Tag color={gradeTagColor(c.grade)} style={{ fontWeight: 700, fontSize: 12, borderRadius: 6, margin: 0 }}>{c.grade}</Tag>
                              <span style={{ fontSize: 11, color: '#7b8499', whiteSpace: 'nowrap' }}>{c.percentile_label} pct.</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 6, background: '#f0f2f5', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(c.overall_avg || 0, 100)}%`, height: '100%', background: scoreColor(c.overall_avg || 0), borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', minWidth: 30 }}>{c.overall_avg ?? '—'}</span>
                          </div>
                        </div>
                      </CandidateRow>
                    ))
                  }
                </CandidateList>
              </ChartCard>
            </SplitGrid>

            <ChartsRow>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Corporate — score breakdown (top {Math.min(10, topN)})</ChartCardTitle>
                </ChartCardHeader>
                {(topCandidates.corporate || []).length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={290}>
                      <BarChart
                        data={(topCandidates.corporate || []).slice(0, 10).map(c => ({
                          name: shortCandidateLabel(c.full_name, c.primary_email),
                          fullLabel: c.full_name || c.primary_email || '',
                          Comm: c.comm_score, Apt: c.apt_score, Psy: c.psy_score,
                          AI_Interview: c.ai_score, Custom: c.custom_score,
                        }))}
                        margin={{ top: 4, right: 8, left: -16, bottom: 72 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <RTooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {[['Comm', 'Communication'], ['Apt', 'Aptitude'], ['Psy', 'Role_Based'], ['AI_Interview', 'AI_Interview'], ['Custom', 'Custom_Assessment']].map(([k, t]) => (
                          <Bar key={k} dataKey={k} fill={typeCfg(t).chart} radius={[2, 2, 0, 0]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Institute — score breakdown (top {Math.min(10, topN)})</ChartCardTitle>
                </ChartCardHeader>
                {(topCandidates.institute || []).length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={290}>
                      <BarChart
                        data={(topCandidates.institute || []).slice(0, 10).map(c => ({
                          name: shortCandidateLabel(c.full_name, c.primary_email),
                          fullLabel: c.full_name || c.primary_email || '',
                          Comm: c.comm_score, Apt: c.apt_score, Psy: c.psy_score,
                          AI_Interview: c.ai_score, Custom: c.custom_score,
                        }))}
                        margin={{ top: 4, right: 8, left: -16, bottom: 72 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <RTooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {[['Comm', 'Communication'], ['Apt', 'Aptitude'], ['Psy', 'Role_Based'], ['AI_Interview', 'AI_Interview'], ['Custom', 'Custom_Assessment']].map(([k, t]) => (
                          <Bar key={k} dataKey={k} fill={typeCfg(t).chart} radius={[2, 2, 0, 0]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>
            </ChartsRow>
          </>
        )
      )}

      {/* ── Practice vs Actual ── */}
      {subTab === 'Practice vs Actual' && (
        pvaLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* KPI strip */}
            <KPIRow>
              {[
                { label: 'Avg Practice Score', value: pvaSummary?.overall_practice_avg ?? '—', icon: <DotChartOutlined />, color: '#94a3b8', trend: 'All types' },
                { label: 'Avg Actual Score',   value: pvaSummary?.overall_actual_avg   ?? '—', icon: <TrophyOutlined />,   color: '#3249D7', trend: 'All types' },
                { label: 'Avg Improvement',    value: pvaSummary?.avg_improvement != null ? `+${pvaSummary.avg_improvement}` : '—', icon: <RiseOutlined />, color: '#22c55e', trend: 'Practice → actual', trendColor: '#22c55e' },
                { label: 'Best Improvement',   value: pvaSummary?.best_value != null ? `+${pvaSummary.best_value}` : '—', icon: <BarChartOutlined />, color: '#f59e0b', trend: pvaSummary?.best_type || '—', trendColor: '#f59e0b' },
                { label: 'Practice Sessions',  value: pvaSummary?.practice_sessions?.toLocaleString() ?? '—', icon: <CalendarOutlined />, color: '#6d28d9', trend: 'Total this period' },
              ].map((k, i) => (
                <KPICard key={i}>
                  <KPIHead>
                    <KPILabel>{k.label}</KPILabel>
                    <KPIIconWrap $color={k.color}>{k.icon}</KPIIconWrap>
                  </KPIHead>
                  <KPIValue style={{ fontSize: 22 }}>{k.value}</KPIValue>
                  {k.trend && <Tooltip title={k.trend}><KPITrend $color={k.trendColor || '#7b8499'}>{k.trend}</KPITrend></Tooltip>}
                </KPICard>
              ))}
            </KPIRow>

            {/* Trend + Improvement breakdown */}
            <ChartsRow>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><LineChartOutlined /> Practice vs actual scores — trend</ChartCardTitle>
                  <Select value={pvaTrendType} onChange={setPvaTrendType} size="small" style={{ width: 110 }} getPopupContainer={t => t.parentNode}>
                    <Option value="all">All Types</Option>
                    {availableTypes.map(k => <Option key={k} value={k}>{k}</Option>)}
                  </Select>
                </ChartCardHeader>
                {pvaTrend.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No trend data</div>
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={pvaTrend} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis domain={[50, 'auto']} tick={{ fontSize: 11 }} />
                        <RTooltip formatter={v => v != null ? v : '—'} />
                        <Legend wrapperStyle={{ fontSize: 12 }} formatter={v => <span style={{ fontWeight: 600 }}>{v}</span>} />
                        <Line type="monotone" dataKey="practice_avg" name="Practice Avg" stroke="#ef4444" strokeDasharray="5 4" dot={{ r: 4 }} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="actual_avg"   name="Actual Avg"   stroke="#1e3a8a" dot={{ r: 4 }} strokeWidth={2} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Avg improvement: practice → actual</ChartCardTitle>
                </ChartCardHeader>
                {(pvaSummary?.breakdown || []).length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={pvaSummary.breakdown} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type_name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RTooltip formatter={v => v != null ? `+${v}` : '—'} />
                        <ReferenceLine y={0} stroke="#e8ecf4" />
                        <Bar dataKey="improvement" name="Improvement" radius={[4, 4, 0, 0]}>
                          {(pvaSummary.breakdown || []).map((d, i) => (
                            <Cell key={i} fill={typeCfg(d.type_name).chart} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>
            </ChartsRow>

            {/* Frequency scatter + Schedule utilisation + Score gap */}
            <ThreeColGrid>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><DotChartOutlined /> Practice frequency impact</ChartCardTitle>
                </ChartCardHeader>
                {pvaFrequency.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <ScatterChart margin={{ top: 4, right: 8, left: -16, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="sessions" name="Practice sessions" type="number" tick={{ fontSize: 10 }} label={{ value: 'Practice sessions', position: 'insideBottom', offset: -12, fontSize: 10 }} />
                        <YAxis dataKey="improvement" name="Score improvement" tick={{ fontSize: 10 }} />
                        <ZAxis range={[20, 20]} />
                        <RTooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [v, n]} />
                        <Scatter data={pvaFrequency} fill="#3249D7" opacity={0.6} />
                      </ScatterChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><CalendarOutlined /> Schedule utilisation</ChartCardTitle>
                </ChartCardHeader>
                <div style={{ padding: '4px 0' }}>
                  {scheduleUtil.length === 0
                    ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                    : scheduleUtil.map(r => (
                      <ProgressRow key={r.label}>
                        <ProgressLabel>{r.label}</ProgressLabel>
                        <ProgressBar><ProgressFill $pct={r.pct} /></ProgressBar>
                        <ProgressCount>{r.used?.toLocaleString()}/{r.total?.toLocaleString()}</ProgressCount>
                      </ProgressRow>
                    ))
                  }
                </div>
              </ChartCard>

              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Score gap analysis</ChartCardTitle>
                </ChartCardHeader>
                {(pvaSummary?.breakdown || []).length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={pvaSummary.breakdown} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type_name" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <RTooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="practice_avg" name="Practice" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="actual_avg"   name="Actual"   radius={[3, 3, 0, 0]}>
                          {(pvaSummary.breakdown || []).map((d, i) => (
                            <Cell key={i} fill={typeCfg(d.type_name).chart} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>
            </ThreeColGrid>
          </>
        )
      )}

      {/* ── Student Growth ── */}
      {subTab === 'Student Growth' && (
        <>
          {/* Filter row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#7b8499', fontWeight: 600 }}>Assessment Type</span>
            <TypeFilterGroup style={{ marginBottom: 0 }}>
              {[['all', 'All Types', '#1a1a2e'], ...availableTypes.map(k => [k, k, typeCfg(k).chart])].map(([v, l, c]) => (
                <TypeFilterBtn key={v} $active={growthTypeFilter === v} $color={c} onClick={() => setGrowthTypeFilter(v)}>{l}</TypeFilterBtn>
              ))}
            </TypeFilterGroup>
            <span style={{ fontSize: 12, color: '#7b8499', fontWeight: 600, marginLeft: 8 }}>Period</span>
            <TypeFilterGroup style={{ marginBottom: 0 }}>
              {[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['annual', 'Annual']].map(([v, l]) => (
                <TypeFilterBtn key={v} $active={growthPeriod === v} $color="#1a1a2e" onClick={() => setGrowthPeriod(v)}>{l}</TypeFilterBtn>
              ))}
            </TypeFilterGroup>
          </div>

          {growthLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
            <>
              {/* Growth trend line chart */}
              <ChartCard style={{ marginBottom: 20 }}>
                <ChartCardHeader>
                  <ChartCardTitle><LineChartOutlined /> Student score growth over time — by assessment type</ChartCardTitle>
                </ChartCardHeader>
                {growthTrend.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No trend data</div>
                  : (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={growthTrend} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                        <RTooltip formatter={v => v != null ? v : '—'} />
                        <Legend wrapperStyle={{ fontSize: 12 }} formatter={v => <span style={{ fontWeight: 600 }}>{v}</span>} />
                        {[
                          ['communication',     'Communication',     typeCfg('Communication').chart],
                          ['aptitude',          'Aptitude',          typeCfg('Aptitude').chart],
                          ['psychometric',      'Role_Based',        typeCfg('Role_Based').chart],
                          ['ai_interview',      'AI_Interview',      typeCfg('AI_Interview').chart],
                          ['custom_assessment', 'Custom_Assessment', typeCfg('Custom_Assessment').chart],
                          // Match by the TYPE_KEYS pill value (n) — old behaviour was
                          // `k.includes(filter.toLowerCase())` which falsely matched
                          // 'psychometric' against 'Role_Based' substring-style.
                        ].filter(([, n]) => growthTypeFilter === 'all' || n === growthTypeFilter).map(([k, n, c]) => (
                          <Line key={k} type="monotone" dataKey={k} name={n} stroke={c} dot={{ r: 4 }} strokeWidth={2} connectNulls />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartsRow>
                {/* Growth rate by entity */}
                <ChartCard>
                  <ChartCardHeader>
                    <ChartCardTitle><BarChartOutlined /> Growth rate by entity type</ChartCardTitle>
                  </ChartCardHeader>
                  {(() => {
                    // Filter to rows where the active entity has a real
                    // value. The backend now returns null (not 0) for the
                    // entity that has no data, so a row like {corp: 100,
                    // inst: null} only shows up under the Corporate Bar.
                    // Without this filter the BarChart silently rendered
                    // invisible zero-height bars when corporate had data
                    // and the user switched to institute (or vice versa).
                    const key = entity === 'corporate' ? 'corporate_growth_pct' : 'institute_growth_pct'
                    const visibleRows = (growthEntity || []).filter(r =>
                      r && r[key] !== null && r[key] !== undefined && !Number.isNaN(r[key])
                    )
                    if (visibleRows.length === 0) {
                      return (
                        <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>
                          No {entity === 'corporate' ? 'corporate' : 'institute'} growth data
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            Needs at least 2 months of submitted scores per assessment type.
                          </div>
                        </div>
                      )
                    }
                    return (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={visibleRows} margin={{ top: 4, right: 8, left: -8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="type_name" tick={{ fontSize: 10 }} />
                          <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                          <RTooltip formatter={v => `${v}%`} />
                          <Legend wrapperStyle={{ fontSize: 12 }} formatter={v => <span style={{ fontWeight: 600 }}>{v}</span>} />
                          <ReferenceLine y={0} stroke="#e8ecf4" />
                          {entity === 'corporate' && (
                            <Bar dataKey="corporate_growth_pct" name="Corporate Growth %" fill="#1e3a8a" radius={[3, 3, 0, 0]} />
                          )}
                          {entity === 'institute' && (
                            <Bar dataKey="institute_growth_pct" name="Institute Growth %" fill="#15803d" radius={[3, 3, 0, 0]} />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    )
                  })()}
                </ChartCard>

                {/* Students showing improvement donut */}
                <ChartCard>
                  <ChartCardHeader>
                    <ChartCardTitle><PieChartOutlined /> Students showing improvement</ChartCardTitle>
                  </ChartCardHeader>
                  {growthDonut.length === 0
                    ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                    : (
                      <>
                        <DonutLegend>
                          {growthDonut.map((d, i) => {
                            const colors = ['#22c55e', '#f59e0b', '#3249D7', '#ef4444']
                            return (
                              <LegendItem key={i}>
                                <LegendSquare $color={colors[i % colors.length]} />
                                {d.label} ({d.count?.toLocaleString()})
                              </LegendItem>
                            )
                          })}
                        </DonutLegend>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={growthDonut} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={58} outerRadius={95} paddingAngle={2}>
                              {growthDonut.map((d, i) => {
                                const colors = ['#22c55e', '#f59e0b', '#3249D7', '#ef4444']
                                return <Cell key={i} fill={colors[i % colors.length]} />
                              })}
                            </Pie>
                            <RTooltip formatter={(v, n) => [v.toLocaleString(), n]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </>
                    )
                  }
                </ChartCard>
              </ChartsRow>

              {/* Growth cohort table */}
              <TableSection>
                <TableSectionHeader>
                  <TableSectionTitle><TableOutlined /> Student growth cohort analysis</TableSectionTitle>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      placeholder="Search student…"
                      value={growthSearch}
                      onChange={e => setGrowthSearch(e.target.value)}
                      style={{ padding: '3px 10px', border: '1px solid #e8ecf4', borderRadius: 8, fontSize: 13, outline: 'none', width: 180 }}
                    />
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        const labels = growthCohort.month_labels || ['M1','M2','M3','M4','M5']
                        exportXlsx(growthCohort.data || [], [
                          { h: 'Student',  k: 'full_name' },
                          { h: 'Entity',   k: 'entity_name' },
                          { h: 'Type',     k: 'entity_type' },
                          ...labels.map((l, i) => ({ h: l, k: `m${i + 1}` })),
                          { h: 'Growth %', k: 'growth_pct' },
                          { h: 'Status',   k: 'status' },
                        ], 'student_growth_cohort.xlsx')
                      }}
                    >Export</Button>
                  </div>
                </TableSectionHeader>
                <Table
                  dataSource={growthCohort.data || []}
                  rowKey="primary_email"
                  size="small"
                  loading={cohortLoading}
                  scroll={{ x: 900 }}
                  onChange={(_pag, _filters, sorter) => {
                    setGrowthSort(sorter && sorter.order
                      ? { columnKey: sorter.columnKey, order: sorter.order }
                      : { columnKey: undefined, order: undefined })
                  }}
                  pagination={{
                    current: growthPage,
                    pageSize: 20,
                    total: growthCohort.total || 0,
                    onChange: p => setGrowthPage(p),
                    showSizeChanger: false,
                    showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}`,
                  }}
                  columns={[
                    {
                      title: 'STUDENT', dataIndex: 'full_name', key: 'name', width: 160,
                      sorter: true,
                      sortOrder: growthSort.columnKey === 'name' ? growthSort.order : null,
                      render: (v, r) => (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                          <div style={{ fontSize: 11, color: '#7b8499' }}>{r.entity_type}</div>
                        </div>
                      ),
                    },
                    {
                      title: 'ENTITY', dataIndex: 'entity_name', key: 'entity', width: 130,
                      sorter: true,
                      sortOrder: growthSort.columnKey === 'entity' ? growthSort.order : null,
                      render: v => <span style={{ fontSize: 12 }}>{v}</span>,
                    },
                    {
                      title: 'ASSESSMENT TYPE', key: 'atype', width: 150,
                      // The cohort is scoped by growthTypeFilter on the API side, so
                      // render that here instead of the previous hardcoded "Communication"
                      // tag (which lied when any other type, or "all", was selected).
                      render: () => {
                        const label = growthTypeFilter === 'all' ? 'All Types' : growthTypeFilter
                        const cfg   = typeCfg(label)
                        return <Tag color={cfg.tag} style={{ borderRadius: 6, fontSize: 11 }}>{label}</Tag>
                      },
                    },
                    ...(growthCohort.month_labels || ['M1','M2','M3','M4','M5']).map((lbl, i) => ({
                      title: lbl.toUpperCase(), dataIndex: `m${i + 1}`, key: `m${i + 1}`, align: 'center', width: 64,
                      render: v => v != null
                        ? <span style={{ fontWeight: 600, fontSize: 12, color: scoreColor(v) }}>{v}</span>
                        : <span style={{ color: '#bbb' }}>—</span>,
                    })),
                    {
                      title: 'GROWTH', dataIndex: 'growth_pct', key: 'growth', align: 'center', width: 80,
                      sorter: true,
                      sortOrder: growthSort.columnKey === 'growth' ? growthSort.order : null,
                      render: v => v != null ? (
                        <span style={{ fontWeight: 700, color: v >= 0 ? '#22c55e' : '#ef4444' }}>
                          {v >= 0 ? '+' : ''}{v}
                        </span>
                      ) : <span style={{ color: '#bbb' }}>—</span>,
                    },
                    {
                      title: 'STATUS', dataIndex: 'status', key: 'status', align: 'center', width: 110,
                      sorter: true,
                      sortOrder: growthSort.columnKey === 'status' ? growthSort.order : null,
                      render: v => {
                        const cfg = { 'High Growth': 'green', 'Moderate': 'gold', 'Stable': 'blue', 'Declining': 'red' }
                        return v ? <Tag color={cfg[v] || 'default'} style={{ fontWeight: 700, borderRadius: 6, fontSize: 11, margin: 0 }}>{v}</Tag> : null
                      },
                    },
                  ]}
                />
              </TableSection>
            </>
          )}
        </>
      )}

      {/* ── Performance ── */}
      {subTab === 'Performance' && (
        <>
          {perfLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
            <ChartsRow style={{ marginBottom: 20 }}>
              {/* Horizontal bar — by entity */}
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Performance by {perfViewBy}</ChartCardTitle>
                </ChartCardHeader>
                {perfBarData.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={Math.max(200, perfBarData.length * 34)}>
                      <BarChart data={perfBarData} layout="vertical" margin={{ top: 4, right: 40, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" domain={[50, 'auto']} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} tickFormatter={v => v?.length > 16 ? v.slice(0, 16) + '…' : v} />
                        <RTooltip formatter={v => v} />
                        <Bar dataKey="avg_score" name="Avg Score" fill="#1e3a8a" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11, fontWeight: 700 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              {/* Radar — skills */}
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle>🎯 Skills radar — top vs avg student</ChartCardTitle>
                </ChartCardHeader>
                {perfRadar.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                  : (
                    <>
                      <DonutLegend style={{ justifyContent: 'center', marginBottom: 4 }}>
                        {[['Top Student','#f59e0b'],['Avg Student','#94a3b8'],['Below Avg','#ef4444']].map(([l,c]) => (
                          <LegendItem key={l}><LegendSquare $color={c} />{l}</LegendItem>
                        ))}
                      </DonutLegend>
                      <ResponsiveContainer width="100%" height={260}>
                        <RadarChart data={perfRadar} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} tickCount={5} />
                          <Radar name="Top Student" dataKey="top_student" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} dot={{ r: 3 }} />
                          <Radar name="Avg Student" dataKey="avg_student" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.15} strokeWidth={2} dot={{ r: 3 }} />
                          <Radar name="Below Avg"   dataKey="below_avg"   stroke="#ef4444" fill="#ef4444" fillOpacity={0.10} strokeWidth={2} dot={{ r: 3 }} />
                          <RTooltip formatter={v => v != null ? v : '—'} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </>
                  )
                }
              </ChartCard>
            </ChartsRow>
          )}

          {/* Performance detail table */}
          <TableSection>
            <TableSectionHeader>
              <TableSectionTitle><TableOutlined /> Performance detail view</TableSectionTitle>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  placeholder="Search name / email…"
                  value={perfSearch}
                  onChange={e => setPerfSearch(e.target.value)}
                  style={{ padding: '3px 10px', border: '1px solid #e8ecf4', borderRadius: 8, fontSize: 13, outline: 'none', width: 200 }}
                />
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => exportXlsx(perfDetail.data || [], [
                    { h: 'Student',   k: 'full_name' },
                    { h: 'Email',     k: 'primary_email' },
                    { h: 'Entity',    k: 'entity_type' },
                    { h: 'Best Type', k: 'best_type' },
                    { h: 'Comm',      k: 'comm_score' },
                    { h: 'Apt',       k: 'apt_score' },
                    { h: 'Role_Based',  k: 'psy_score' },
                    { h: 'AI_Interview',     k: 'ai_score' },
                    { h: 'Custom_Assessment', k: 'custom_score' },
                    { h: 'Overall',   k: 'overall_avg' },
                  ], 'performance_detail.xlsx')}
                >Export</Button>
              </div>
            </TableSectionHeader>
            <Table
              dataSource={perfDetail.data || []}
              rowKey="primary_email"
              size="small"
              loading={perfTableLoad}
              scroll={{ x: 1050 }}
              pagination={{
                current: perfPage,
                pageSize: 20,
                total: perfDetail.total || 0,
                onChange: p => setPerfPage(p),
                showSizeChanger: false,
                showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}`,
              }}
              columns={[
                {
                  title: 'STUDENT', dataIndex: 'full_name', key: 'name', width: 150,
                  render: v => <span style={{ fontWeight: 700, fontSize: 13 }}>{v}</span>,
                },
                {
                  title: 'EMAIL', dataIndex: 'primary_email', key: 'email',
                  render: v => v
                    ? <span style={{ fontSize: 12, color: '#1a1a2e', wordBreak: 'break-all' }}>{v}</span>
                    : <span style={{ color: '#bbb' }}>—</span>,
                },
                {
                  title: 'ENTITY', dataIndex: 'entity_type', key: 'entity', width: 100,
                  render: v => <Tag color={v === 'Institute' ? 'blue' : 'purple'} style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{v}</Tag>,
                },
                {
                  title: 'TYPE', dataIndex: 'best_type', key: 'type', width: 130,
                  render: v => v ? <Tag color={typeCfg(v).tag} style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{v}</Tag> : null,
                },
                { title: 'COMM',     dataIndex: 'comm_score',     key: 'comm',    align: 'center', width: 62, render: v => <span style={{ fontWeight: 600, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span> },
                { title: 'APT',      dataIndex: 'apt_score',      key: 'apt',     align: 'center', width: 62, render: v => <span style={{ fontWeight: 600, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span> },
                { title: 'ROLE_BASED',  dataIndex: 'psy_score',      key: 'psy',     align: 'center', width: 90, render: v => <span style={{ fontWeight: 600, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span> },
                { title: 'AI_INTERVIEW',      dataIndex: 'ai_score',     key: 'ai',     align: 'center', width: 110, render: v => <span style={{ fontWeight: 600, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span> },
                { title: 'CUSTOM_ASSESSMENT', dataIndex: 'custom_score', key: 'custom', align: 'center', width: 140, render: v => <span style={{ fontWeight: 600, color: v != null ? scoreColor(v) : '#bbb' }}>{v ?? '—'}</span> },
                {
                  title: 'OVERALL', dataIndex: 'overall_avg', key: 'overall', align: 'center', width: 120,
                  render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : <span style={{ color: '#bbb' }}>—</span>,
                },
                {
                  title: 'PERCENTILE', dataIndex: 'percentile_label', key: 'pct', align: 'center', width: 90,
                  render: v => <span style={{ fontWeight: 700, color: '#f59e0b', fontSize: 12 }}>{v}</span>,
                },
              ]}
            />
          </TableSection>
        </>
      )}

      {/* ── Analytics ── */}
      {subTab === 'Analytics' && (
        analyticsLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* Two donuts: type split (entity-scoped) + completion rates.
                Override the 3-col grid to 2-col so the cards span the row
                evenly without an empty third column. */}
            <ThreeColGrid style={{ gridTemplateColumns: '1fr 1fr' }}>
              {/* Type split — the chart follows the page-level Entity
                  selector instead of rendering both side-by-side. Showing
                  both produced an empty "no data" panel for the entity
                  that isn't in scope, which read like a bug. */}
              {(() => {
                const isCorp = entity === 'corporate'
                const rows   = isCorp ? analyticsCorpData : analyticsInstData
                const countKey = isCorp ? 'corporate_count' : 'institute_count'
                const title  = isCorp ? 'Corporate type split' : 'Institute type split'
                return (
                  <ChartCard>
                    <ChartCardHeader><ChartCardTitle><PieChartOutlined /> {title}</ChartCardTitle></ChartCardHeader>
                    {rows.length === 0
                      ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                      : (
                        <>
                          <DonutLegend>
                            {rows.map(d => (
                              <LegendItem key={d.type_name}>
                                <LegendSquare $color={typeCfg(d.type_name).chart} />
                                {d.type_name} {d[countKey]?.toLocaleString()}
                              </LegendItem>
                            ))}
                          </DonutLegend>
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie data={rows.map(d => ({ name: d.type_name, value: d[countKey] }))} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                                {rows.map((d, i) => <Cell key={i} fill={typeCfg(d.type_name).chart} />)}
                              </Pie>
                              <RTooltip formatter={(v, n) => [v.toLocaleString(), n]} />
                            </PieChart>
                          </ResponsiveContainer>
                        </>
                      )
                    }
                  </ChartCard>
                )
              })()}

              {/* Completion rates — Hinglish merged into Communication */}
              <ChartCard>
                <ChartCardHeader><ChartCardTitle>〆 Completion rates</ChartCardTitle></ChartCardHeader>
                {analyticsCompletionData.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                  : (
                    <>
                      <DonutLegend>
                        {analyticsCompletionData.map(d => (
                          <LegendItem key={d.type_name}>
                            <LegendSquare $color={typeCfg(d.type_name).chart} />
                            {d.type_name} {d.completion_rate}% <span style={{ color: '#94a3b8' }}>({d.submitted_count ?? 0}/{d.total_count ?? 0})</span>
                          </LegendItem>
                        ))}
                      </DonutLegend>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          {/* Slice size = total assigned (volume). When every type
                              has 100 % completion the percentage donut would render
                              5 identical slices; sizing by volume makes the donut
                              actually informative even at full completion. */}
                          <Pie data={analyticsCompletionData.map(d => ({ name: d.type_name, value: d.total_count || 0, submitted: d.submitted_count || 0, rate: d.completion_rate }))} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                            {analyticsCompletionData.map((d, i) => <Cell key={i} fill={typeCfg(d.type_name).chart} />)}
                          </Pie>
                          <RTooltip formatter={(v, n, ctx) => [`${ctx?.payload?.rate ?? 0}% (${ctx?.payload?.submitted ?? 0}/${v})`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </>
                  )
                }
              </ChartCard>
            </ThreeColGrid>

            {/* Weekly stacked activity */}
            <ChartCard style={{ marginBottom: 20 }}>
              <ChartCardHeader>
                <ChartCardTitle><BarChartOutlined /> Weekly stacked assessment activity — last 12 weeks</ChartCardTitle>
              </ChartCardHeader>
              {analyticsWeekly.length === 0
                ? <div style={{ color: '#bbb', textAlign: 'center', padding: '40px 0' }}>No data</div>
                : (
                  <>
                    <DonutLegend style={{ marginBottom: 8 }}>
                      {TYPE_KEYS.map(k => <LegendItem key={k}><LegendSquare $color={typeCfg(k).chart} />{k}</LegendItem>)}
                    </DonutLegend>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analyticsWeekly} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RTooltip />
                        {TYPE_KEYS.map(k => (
                          <Bar key={k} dataKey={k} stackId="a" fill={typeCfg(k).chart} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )
              }
            </ChartCard>

            {/* Candidate count + Completion time */}
            <ChartsRow>
              <ChartCard>
                <ChartCardHeader><ChartCardTitle><TeamOutlined /> Candidate count by type</ChartCardTitle></ChartCardHeader>
                {analyticsCounts.length === 0
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No data</div>
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analyticsCounts} layout="vertical" margin={{ top: 4, right: 40, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="type_name" width={100} tick={{ fontSize: 11 }} />
                        <RTooltip />
                        <Bar dataKey="candidate_count" name="Candidates" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11, fontWeight: 700 }}>
                          {analyticsCounts.map((d, i) => <Cell key={i} fill={typeCfg(d.type_name).chart} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>

              <ChartCard>
                <ChartCardHeader><ChartCardTitle>⏱ Assessment completion time (avg mins)</ChartCardTitle></ChartCardHeader>
                {analyticsCounts.length === 0 || analyticsCounts.every(d => d.avg_completion_mins == null)
                  ? <div style={{ color: '#bbb', textAlign: 'center', padding: '32px 0' }}>No timing data</div>
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analyticsCounts} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type_name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RTooltip formatter={v => v != null ? `${v} min` : '—'} />
                        <Bar dataKey="avg_completion_mins" name="Avg mins" radius={[4, 4, 0, 0]}>
                          {analyticsCounts.map((d, i) => <Cell key={i} fill={typeCfg(d.type_name).chart} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </ChartCard>
            </ChartsRow>
          </>
        )
      )}

      {/* ── Predictive ────────────────────────────────────────── */}
      {subTab === 'Predictive' && (
        predictiveLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* KPI Row */}
            <KPIRow style={{ marginBottom: 20 }}>
              <div style={{ flex: 1, background: '#0f2a3f', borderRadius: 12, padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7fb5c8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Predicted Avg Score — Next Month</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: 6 }}>{predictiveKPIs?.predicted_avg ?? '—'}</div>
                <div style={{ fontSize: 12, color: '#7fb5c8' }}>
                  {predictiveKPIs?.delta != null ? `${predictiveKPIs.delta >= 0 ? '+' : ''}${predictiveKPIs.delta} pts from current avg` : '—'}
                  {predictiveKPIs?.confidence_interval != null ? ` · 94% confidence interval ±${predictiveKPIs.confidence_interval}` : ''}
                </div>
                <div style={{ position: 'absolute', right: 16, top: 12, fontSize: 60, fontWeight: 900, color: '#ffffff08', letterSpacing: '-4px', pointerEvents: 'none' }}>PREDICTIVE</div>
              </div>
              <div style={{ flex: 1, background: '#0e6b5e', borderRadius: 12, padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7dd4c8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Forecasted Completion Rate</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: 6 }}>{predictiveKPIs?.completion_rate != null ? `${predictiveKPIs.completion_rate}%` : '—'}</div>
                <div style={{ fontSize: 12, color: '#7dd4c8' }}>
                  {predictiveKPIs?.prev_completion_rate != null ? `Up from ${predictiveKPIs.prev_completion_rate}%` : '—'}
                  {' · Based on 5-month trend regression'}
                </div>
                <div style={{ position: 'absolute', right: 16, top: 12, fontSize: 60, fontWeight: 900, color: '#ffffff08', letterSpacing: '-4px', pointerEvents: 'none' }}>PREDICTIVE</div>
              </div>
              <div style={{ flex: 1, background: '#5b21b6', borderRadius: 12, padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#c4a8f0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>At-Risk Candidates</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: 6 }}>{fmt(predictiveKPIs?.at_risk_count)}</div>
                <div style={{ fontSize: 12, color: '#c4a8f0' }}>Predicted to score below 50 in next cycle · {predictiveKPIs?.flagged_institutes ?? 0} institutes flagged</div>
                <div style={{ position: 'absolute', right: 16, top: 12, fontSize: 60, fontWeight: 900, color: '#ffffff08', letterSpacing: '-4px', pointerEvents: 'none' }}>PREDICTIVE</div>
              </div>
            </KPIRow>

            {/* Forecast + Scatter + Feature Importance */}
            <ChartsRow style={{ gridTemplateColumns: '38fr 32fr 30fr' }}>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><LineChartOutlined /> Score forecast — next 3 months</ChartCardTitle>
                </ChartCardHeader>
                <DonutLegend>
                  <LegendItem><LegendSquare $color="#1a1a2e" />Historical Actual</LegendItem>
                  <LegendItem><LegendSquare $color="#22c55e" />Predicted</LegendItem>
                  <LegendItem><LegendSquare $color="#bbf7d0" />Confidence Interval</LegendItem>
                </DonutLegend>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={predictiveForecast} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[50, 85]} tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Line dataKey="historical" name="Historical" stroke="#1a1a2e" strokeWidth={2} dot={{ r: 4 }} connectNulls={false} />
                    <Line dataKey="predicted"  name="Predicted"  stroke="#22c55e" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 4 }} connectNulls={false} />
                    <Line dataKey="upper"      name="Upper CI"   stroke="#bbf7d0" strokeWidth={1} dot={false} connectNulls={false} />
                    <Line dataKey="lower"      name="Lower CI"   stroke="#bbf7d0" strokeWidth={1} dot={false} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><AlertOutlined /> At-risk student segmentation</ChartCardTitle>
                </ChartCardHeader>
                <DonutLegend>
                  {[['High Risk','#ef4444'],['Medium Risk','#f97316'],['Low Risk','#22c55e']].map(([l,c]) => (
                    <LegendItem key={l}><LegendSquare $color={c} />{l}</LegendItem>
                  ))}
                </DonutLegend>
                <ResponsiveContainer width="100%" height={220}>
                  <ScatterChart margin={{ top: 4, right: 12, left: -10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                    <XAxis dataKey="practice_pct" name="Practice completion %" type="number" domain={[0,100]} tick={{ fontSize: 10 }} label={{ value: 'Practice completion %', position: 'insideBottom', offset: -14, fontSize: 10 }} />
                    <YAxis dataKey="predicted_score" name="Predicted score" type="number" domain={[20, 100]} tick={{ fontSize: 10 }} />
                    <ZAxis dataKey="size" range={[40, 200]} />
                    <RTooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [v, n]} />
                    {[['High Risk','#ef4444'],['Medium Risk','#f97316'],['Low Risk','#22c55e']].map(([level, color]) => (
                      <Scatter key={level} name={level}
                        data={predictiveAtRisk.filter(d => d.risk_level === level)}
                        fill={color} fillOpacity={0.65} />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BarChartOutlined /> Feature importance — score predictors</ChartCardTitle>
                </ChartCardHeader>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={predictiveFeatures} layout="vertical" margin={{ top: 4, right: 32, left: 70, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f2f5" />
                    <XAxis type="number" domain={[0, 40]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="feature" tick={{ fontSize: 10 }} width={68} />
                    <RTooltip formatter={v => `${v}%`} />
                    <Bar dataKey="importance" fill="#1a1a2e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsRow>

            {/* At-Risk list — full width */}
            <TableSection style={{ margin: 0 }}>
              <TableSectionHeader>
                <TableSectionTitle><TableOutlined /> At-risk candidate list — predicted low scorers</TableSectionTitle>
                <input
                  placeholder="Search name / email…"
                  value={predictiveSearch}
                  onChange={e => setPredictiveSearch(e.target.value)}
                  style={{ padding: '3px 10px', border: '1px solid #e8ecf4', borderRadius: 8, fontSize: 13, outline: 'none', width: 200 }}
                />
              </TableSectionHeader>
              <Table
                dataSource={predictiveAtRiskList.data}
                rowKey={(r) => r.primary_email || r.full_name}
                loading={false}
                size="small"
                pagination={{
                  total: predictiveAtRiskList.total, pageSize: 20,
                  current: predictiveListPage, showSizeChanger: false, size: 'small',
                  onChange: p => setPredictiveListPage(p),
                }}
                columns={[
                  {
                    title: 'Name', dataIndex: 'full_name', key: 'full_name',
                    render: v => <span style={{ fontWeight: 600, fontSize: 13 }}>{v}</span>,
                  },
                  {
                    title: 'Email', dataIndex: 'primary_email', key: 'email',
                    render: v => v
                      ? <span style={{ fontSize: 12, color: '#1a1a2e', wordBreak: 'break-all' }}>{v}</span>
                      : <span style={{ color: '#bbb' }}>—</span>,
                  },
                  { title: 'Institute', dataIndex: 'entity_name', key: 'entity', render: v => <span style={{ fontSize: 12, color: '#7b8499' }}>{v?.length > 18 ? v.slice(0, 18) + '…' : v}</span> },
                  { title: 'Type', dataIndex: 'assessment_type', key: 'type', render: v => <Tag color={typeCfg(v).tag} style={{ fontSize: 11 }}>{v}</Tag> },
                  { title: 'Current Avg', dataIndex: 'current_avg', key: 'curr', render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Predicted', dataIndex: 'predicted_score', key: 'pred', render: v => <span style={{ fontWeight: 700, color: '#f97316' }}>{v}</span> },
                  { title: 'Risk', dataIndex: 'risk', key: 'risk', render: v => <Tag color={v === 'High' ? 'red' : v === 'Medium' ? 'orange' : 'green'}>{v}</Tag> },
                  { title: 'Recommendation', dataIndex: 'recommendation', key: 'rec', render: v => <span style={{ fontSize: 11, color: '#7b8499' }}>{v}</span> },
                ]}
              />
            </TableSection>
          </>
        )
      )}

      {/* ── Cross-Institute ──────────────────────────────────── */}
      {subTab === 'Cross-Institute' && (
        crossLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* Sub-filter bar */}
            <FilterBar style={{ marginBottom: 16 }}>
              <FilterLabel>Metric</FilterLabel>
              <StatusToggleGroup>
                {[['avg_score','Avg Score'],['top_score','Top Score'],['pass_rate','Pass Rate'],['growth','Growth']].map(([v, l]) => (
                  <StatusToggle key={v} $active={crossMetric === v} onClick={() => setCrossMetric(v)}>{l}</StatusToggle>
                ))}
              </StatusToggleGroup>
            </FilterBar>

            {/* Bar chart */}
            <ChartCard style={{ marginBottom: 16 }}>
              <ChartCardHeader>
                <ChartCardTitle><BarChartOutlined /> Candidate scores across institutes</ChartCardTitle>
              </ChartCardHeader>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={crossScores} margin={{ top: 4, right: 12, left: -10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                  <XAxis dataKey="institute" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0}
                    tickFormatter={v => v?.length > 18 ? v.slice(0, 18) + '…' : v} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Bar dataKey="value" name={crossMetric === 'pass_rate' ? 'Pass Rate %' : crossMetric === 'growth' ? 'Growth pts' : crossMetric === 'top_score' ? 'Top Score' : 'Avg Score'}
                    radius={[4, 4, 0, 0]}>
                    {crossScores.map((d, i) => (
                      <Cell key={i} fill={i % 2 === 0 ? '#1a1a2e' : '#c97c2a'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Radar + Percentile */}
            <SplitGrid>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><DotChartOutlined /> Institute competency radar</ChartCardTitle>
                </ChartCardHeader>
                {(() => {
                  const institutes = crossRadar.length > 0
                    ? Object.keys(crossRadar[0]).filter(k => k !== 'axis') : []
                  const INST_COLORS = ['#1a1a2e','#c97c2a','#3249D7','#22c55e','#ef4444']
                  return (
                    <>
                      <DonutLegend>
                        {institutes.map((inst, i) => (
                          <LegendItem key={inst}><LegendSquare $color={INST_COLORS[i % INST_COLORS.length]} />{inst?.length > 12 ? inst.slice(0,12)+'…' : inst}</LegendItem>
                        ))}
                      </DonutLegend>
                      <ResponsiveContainer width="100%" height={240}>
                        <RadarChart data={crossRadar} margin={{ top: 4, right: 20, left: 20, bottom: 4 }}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                          {institutes.map((inst, i) => (
                            <Radar key={inst} name={inst} dataKey={inst}
                              stroke={INST_COLORS[i % INST_COLORS.length]}
                              fill={INST_COLORS[i % INST_COLORS.length]} fillOpacity={0.10} strokeWidth={2} />
                          ))}
                          <Legend wrapperStyle={{ fontSize: 11 }} formatter={v => v?.length > 14 ? v.slice(0,14)+'…' : v} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </>
                  )
                })()}
              </ChartCard>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><LineChartOutlined /> Score percentile distribution</ChartCardTitle>
                </ChartCardHeader>
                {(() => {
                  const institutes = crossPercentile.length > 0
                    ? Object.keys(crossPercentile[0]).filter(k => k !== 'percentile') : []
                  const INST_COLORS = ['#1a1a2e','#22c55e','#3249D7','#c97c2a']
                  return (
                    <>
                      <DonutLegend>
                        {institutes.map((inst, i) => (
                          <LegendItem key={inst}><LegendSquare $color={INST_COLORS[i % INST_COLORS.length]} />{inst?.length > 10 ? inst.slice(0,10)+'…' : inst}</LegendItem>
                        ))}
                      </DonutLegend>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={crossPercentile} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                          <XAxis dataKey="percentile" tick={{ fontSize: 10 }} />
                          <YAxis domain={[20, 100]} tick={{ fontSize: 11 }} />
                          <RTooltip />
                          {institutes.map((inst, i) => (
                            <Line key={inst} dataKey={inst} stroke={INST_COLORS[i % INST_COLORS.length]}
                              strokeWidth={2} dot={{ r: 3 }} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </>
                  )
                })()}
              </ChartCard>
            </SplitGrid>

            {/* Benchmarking Table */}
            <TableSection>
              <TableSectionHeader>
                <TableSectionTitle><TableOutlined /> Institute score benchmarking table</TableSectionTitle>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  style={{ background: '#166534', color: '#fff', border: 'none' }}
                  onClick={() => exportXlsx(crossTable || [], [
                    { h: 'Rank',          k: 'rank' },
                    { h: 'Institute',     k: 'institute' },
                    { h: 'State',         k: 'state' },
                    { h: 'Candidates',    k: 'candidates' },
                    { h: 'Comm Avg',      k: 'comm_avg' },
                    { h: 'Apt Avg',       k: 'apt_avg' },
                    { h: 'Psy Avg',       k: 'psy_avg' },
                    { h: 'AI_Interview Avg',      k: 'ai_avg' },
                    { h: 'Custom_Assessment Avg', k: 'custom_avg' },
                    { h: 'Overall Avg',   k: 'overall_avg' },
                    { h: 'Pass Rate %',   k: 'pass_rate' },
                    { h: 'Top Score',     k: 'top_score' },
                  ], 'cross_institute_benchmark.xlsx')}
                >Export</Button>
              </TableSectionHeader>
              <Table
                dataSource={crossTable}
                rowKey="institute"
                size="small"
                pagination={false}
                scroll={{ x: 900 }}
                columns={[
                  { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 55, render: v => (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: v === 1 ? '#f59e0b' : v === 2 ? '#94a3b8' : v === 3 ? '#c97c2a' : '#e8ecf4', color: v <= 3 ? '#fff' : '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{v}</div>
                  )},
                  { title: 'Institute', dataIndex: 'institute', key: 'inst', render: v => <span style={{ fontWeight: 700, fontSize: 13 }}>{v}</span> },
                  { title: 'State', dataIndex: 'state', key: 'state', width: 100 },
                  { title: 'Candidates', dataIndex: 'candidates', key: 'cands', width: 90, render: v => fmt(v) },
                  { title: 'Comm Avg', dataIndex: 'comm_avg', key: 'comm', width: 90, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Apt Avg', dataIndex: 'apt_avg', key: 'apt', width: 90, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Psy Avg', dataIndex: 'psy_avg', key: 'psy', width: 90, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'AI Interview Avg',     dataIndex: 'ai_avg',     key: 'ai',     width: 130, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Custom Assessment Avg', dataIndex: 'custom_avg', key: 'custom', width: 160, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Overall Avg', dataIndex: 'overall_avg', key: 'overall', width: 110, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Pass Rate', dataIndex: 'pass_rate', key: 'pass', width: 90, render: v => v != null ? <Tag color={v >= 70 ? 'green' : v >= 50 ? 'orange' : 'red'}>{v}%</Tag> : '—' },
                  { title: 'Top Score', dataIndex: 'top_score', key: 'top', width: 90, render: v => <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{v ?? '—'}</span> },
                ]}
              />
            </TableSection>
          </>
        )
      )}

      {/* ── Schedule ─────────────────────────────────────────── */}
      {subTab === 'Schedule' && (
        scheduleLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* KPI Cards — reuse scheduleUtil from Practice vs Actual */}
            <KPIRow style={{ marginBottom: 20 }}>
              {[
                { label: 'Daily',     field: 'daily',     sub: 'Avg/day' },
                { label: 'Weekly',    field: 'weekly',    sub: 'This week' },
                { label: 'Monthly',   field: 'monthly',   sub: 'This month' },
                { label: 'Quarterly', field: 'quarterly', sub: 'Q2 2026' },
                { label: 'Annual',    field: 'annual',    sub: 'YTD projected' },
              ].map(({ label, field, sub }) => {
                const row = scheduleUtil.find(r => r.label?.toLowerCase() === field) || {}
                return (
                  <KPICard key={field}>
                    <KPIHead><KPILabel>{label.toUpperCase()}</KPILabel></KPIHead>
                    <KPIValue>{row.used != null ? fmt(row.used) : row.count != null ? fmt(row.count) : '—'}</KPIValue>
                    <KPITrend>{sub}</KPITrend>
                  </KPICard>
                )
              })}
            </KPIRow>

            {/* Chart */}
            <ChartCard>
              <ChartCardHeader>
                <ChartCardTitle><CalendarOutlined /> Generated vs used — by schedule period</ChartCardTitle>
                <StatusToggleGroup>
                  {['daily','weekly','monthly','quarterly','annual'].map(p => (
                    <StatusToggle key={p} $active={scheduleChartPeriod === p} onClick={() => setScheduleChartPeriod(p)} style={{ textTransform: 'capitalize' }}>{p.charAt(0).toUpperCase() + p.slice(1)}</StatusToggle>
                  ))}
                </StatusToggleGroup>
              </ChartCardHeader>
              <DonutLegend>
                <LegendItem><LegendSquare $color="#1a1a2e" />Generated</LegendItem>
                <LegendItem><LegendSquare $color="#15803d" />Used</LegendItem>
              </DonutLegend>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={scheduleChart} margin={{ top: 4, right: 12, left: -10, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Bar dataKey="generated" name="Generated" fill="#1a1a2e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="used"      name="Used"      fill="#15803d" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </>
        )
      )}

      {/* ── Entities ─────────────────────────────────────────── */}
      {subTab === 'Entities' && (
        entitiesLoading ? <SpinWrap><Spin size="large" /></SpinWrap> : (
          <>
            {/* Two bar charts */}
            <SplitGrid>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><BankOutlined /> Corporate entities</ChartCardTitle>
                </ChartCardHeader>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={entitiesCharts.corporate} margin={{ top: 4, right: 12, left: -10, bottom: 36 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                    <XAxis dataKey="entity" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" interval={0}
                      tickFormatter={v => v?.length > 14 ? v.slice(0,14)+'…' : v} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Bar dataKey="count" name="Assessments" fill="#1a1a2e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard>
                <ChartCardHeader>
                  <ChartCardTitle><ApartmentOutlined /> Institute entities</ChartCardTitle>
                </ChartCardHeader>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={entitiesCharts.institute} margin={{ top: 4, right: 12, left: -10, bottom: 36 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                    <XAxis dataKey="entity" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" interval={0}
                      tickFormatter={v => v?.length > 14 ? v.slice(0,14)+'…' : v} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip />
                    <Bar dataKey="count" name="Assessments" fill="#15803d" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </SplitGrid>

            {/* Entities Table */}
            <TableSection>
              <TableSectionHeader>
                <TableSectionTitle><TableOutlined /> {entitiesFilter === 'corporate' ? 'Corporate' : 'Institute'} entities — utilisation &amp; scores</TableSectionTitle>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Select value={entitiesFilter} onChange={v => setEntitiesFilter(v)} size="small" style={{ width: 110 }}>
                    <Option value="corporate">Corporate</Option>
                    <Option value="institute">Institute</Option>
                  </Select>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    style={{ background: '#166534', color: '#fff', border: 'none' }}
                    onClick={() => exportXlsx(entitiesTable || [], ENTITIES_COLS, `assessment_entities_${new Date().toISOString().slice(0,10)}.xlsx`)}
                  >Export</Button>
                </div>
              </TableSectionHeader>
              <Table
                dataSource={entitiesTable}
                rowKey="entity_name"
                size="small"
                pagination={false}
                scroll={{ x: 1100 }}
                columns={[
                  { title: 'Entity', dataIndex: 'entity_name', key: 'entity', width: 160, render: v => <span style={{ fontWeight: 700, fontSize: 13 }}>{v}</span> },
                  { title: 'Type', dataIndex: 'entity_type', key: 'type', width: 90, render: v => <Tag color={v === 'Corporate' ? 'blue' : 'green'} style={{ fontSize: 11 }}>{v}</Tag> },
                  { title: 'State', dataIndex: 'state', key: 'state', width: 90 },
                  { title: 'Total', dataIndex: 'total_assessments', key: 'total', width: 70, render: v => fmt(v) },
                  { title: 'Comm', dataIndex: 'comm_avg', key: 'comm', width: 90, render: (v, r) => v != null ? <InlineBar numVal={v} displayVal={<><span style={{ fontSize: 10, color: '#7b8499' }}>{r.comm_count}</span> {v}</>} color={scoreColor(v)} /> : '—' },
                  { title: 'Apt', dataIndex: 'apt_avg', key: 'apt', width: 90, render: (v, r) => v != null ? <InlineBar numVal={v} displayVal={<><span style={{ fontSize: 10, color: '#7b8499' }}>{r.apt_count}</span> {v}</>} color={scoreColor(v)} /> : '—' },
                  { title: 'Role_Based',  dataIndex: 'psy_avg',      key: 'psy',   width: 110, render: (v, r) => v != null ? <InlineBar numVal={v} displayVal={<><span style={{ fontSize: 10, color: '#7b8499' }}>{r.psy_count}</span> {v}</>} color={scoreColor(v)} /> : '—' },
                  { title: 'AI Interview',      dataIndex: 'ai_avg',     key: 'ai',     width: 130, render: (v, r) => v != null ? <InlineBar numVal={v} displayVal={<><span style={{ fontSize: 10, color: '#7b8499' }}>{r.ai_count}</span> {v}</>} color={scoreColor(v)} /> : '—' },
                  { title: 'Custom Assessment', dataIndex: 'custom_avg', key: 'custom', width: 160, render: (v, r) => v != null ? <InlineBar numVal={v} displayVal={<><span style={{ fontSize: 10, color: '#7b8499' }}>{r.custom_count}</span> {v}</>} color={scoreColor(v)} /> : '—' },
                  { title: 'Avg Score', dataIndex: 'avg_score', key: 'avg', width: 110, render: v => v != null ? <InlineBar numVal={v} displayVal={v} color={scoreColor(v)} /> : '—' },
                  { title: 'Utilisation', dataIndex: 'utilisation_pct', key: 'util', width: 110, render: v => v != null ? <InlineBar numVal={v} displayVal={`${v}%`} color={utilColor(v)} /> : '—' },
                  { title: 'Status', dataIndex: 'status', key: 'status', width: 110, render: v => (
                    <Tag color={v === 'Active' ? 'green' : v === 'Expired' ? 'red' : 'orange'} style={{ fontSize: 11 }}>{v}</Tag>
                  )},
                ]}
              />
            </TableSection>
          </>
        )
      )}

      {/* Coming soon for any truly unimplemented sub-tabs */}
      {!['Overview', 'Scores & Rankings', 'Top Candidates', 'Practice vs Actual', 'Student Growth',
          'Performance', 'Analytics', 'Predictive', 'Cross-Institute', 'Schedule', 'Entities'].includes(subTab) && (
        <ComingSoon>
          <BarChartOutlined style={{ fontSize: 48, display: 'block', margin: '0 auto 16px' }} />
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Coming Soon</div>
          <div style={{ fontSize: 13 }}>{subTab} analytics will be built here</div>
        </ComingSoon>
      )}
    </div>
  )
}

export default AssessmentTab
